import { useState, useEffect, useRef, useCallback } from "react";

// ─── ROUTE MAP ────────────────────────────────────────────────────────────────
const VENUS_HANDLE = "marstek-venus-e-3-0-5120-wh-stromspeicher-fur-balkonkraftwerk-ac-gekoppelter-solarspeicher-mit-notstrom-2500w-lade-entladeleistung-ip65-app-gesteuert-erweiterbar-auf-15-36-kwh-venus-e-3-0";
const B2500_HANDLE = "marstek-solarspeicher-b2500d-2025-updated-2240wh-fur-balkonkraftwerk-plug-play-speicher-fur-pv-anlage-2-24kw-stromspeicher-solarbank-fur-haus-balkon-b2500-d-erweiterbar-ip65";

const SUMUP_LINKS = {
  "DO-236060": "https://pay.sumup.com/b2c/QHEP8LS4",   // 349.95€
  "DO-236061": "https://pay.sumup.com/b2c/QFNPQRTN",  // 157.95€
};

const ROUTES = {
  "/": "home",
  "/products": "products",
  [`/products/${VENUS_HANDLE}`]: "product-venus",
  [`/products/${B2500_HANDLE}`]: "product-b2500",
  "/checkout": "checkout",
  "/policies/impressum": "impressum",
  "/policies/contact": "contact",
  "/policies/privacy-policy": "privacy-policy",
  "/policies/terms-of-service": "terms-of-service",
  "/policies/refund-policy": "refund-policy",
  "/policies/shipping-policy": "shipping-policy",
  "/policies/uber-uns": "uber-uns",

  // Backwards-compatible (old German URLs)
  "/impressum": "impressum",
  "/contact": "contact",
  "/privacy-policy": "privacy-policy",
  "/terms-of-service": "terms-of-service",
  "/refund-policy": "refund-policy",
  "/shipping-policy": "shipping-policy",
  "/uber-uns": "uber-uns",
  "/datenschutz": "privacy-policy",
  "/agb": "terms-of-service",
  "/widerruf": "refund-policy",
  "/versand": "shipping-policy",
};

const PAGE_TO_PATH = {
  "home": "/",
  "products": "/products",
  "product-venus": `/products/${VENUS_HANDLE}`,
  "product-b2500": `/products/${B2500_HANDLE}`,
  "checkout": "/checkout",
  "impressum": "/policies/impressum",
  "contact": "/policies/contact",
  "privacy-policy": "/policies/privacy-policy",
  "terms-of-service": "/policies/terms-of-service",
  "refund-policy": "/policies/refund-policy",
  "shipping-policy": "/policies/shipping-policy",
  "uber-uns": "/policies/uber-uns",
};

function pathToPage(path) {
  return ROUTES[path] || "home";
}

// ─── CSS ─────────────────────────────────────────────────────────────────────
const CSS = `
:root{--bg:#f4f6f9;--surface:#ffffff;--surface2:#edf0f5;--border:#00000010;--accent:#7ab800;--accent2:#00a882;--text:#0d1117;--muted:#5a6478;--danger:#e03030;--radius:16px;--radius-sm:10px}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{font-family:'DM Sans',sans-serif;background:var(--bg);color:var(--text);min-height:100vh;overflow-x:hidden;max-width:100vw}
a{color:inherit;text-decoration:none}
body::before{content:'';position:fixed;inset:0;pointer-events:none;z-index:0;opacity:.08;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")}
h1,h2,h3,h4{font-family:'Syne',sans-serif;line-height:1.1;letter-spacing:-0.02em}
.section-label{font-size:.75rem;font-weight:600;letter-spacing:.2em;text-transform:uppercase;color:var(--accent);margin-bottom:1rem}
.container{max-width:1200px;margin:0 auto;padding:0 1.5rem;position:relative;z-index:1;width:100%}
section{padding:6rem 0}
nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:1.2rem 0;transition:all .3s;border-bottom:1px solid transparent}
nav.scrolled{background:rgba(244,246,249,.95);backdrop-filter:blur(20px);border-color:var(--border)}
.nav-inner{display:flex;align-items:center;justify-content:space-between;gap:2rem}
.logo{font-family:'Syne',sans-serif;font-weight:800;font-size:1.4rem;letter-spacing:-.03em;display:flex;align-items:center;gap:.5rem;cursor:pointer;text-decoration:none;color:inherit}
.logo span{color:var(--accent)}
.logo-icon{width:32px;height:32px;background:var(--accent);border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.nav-links{display:flex;align-items:center;gap:2rem}
.nav-links a{font-size:.9rem;color:var(--muted);transition:.2s;font-weight:500}
.nav-links a:hover{color:var(--text)}
.nav-cta{background:var(--accent);color:#000;padding:.55rem 1.4rem;border-radius:50px;font-size:.875rem;font-weight:700;font-family:'Syne',sans-serif;transition:.2s;border:none;cursor:pointer;text-decoration:none;display:inline-block;white-space:nowrap}
.nav-cta:hover{transform:scale(1.04)}
.hamburger{display:none;flex-direction:column;gap:5px;cursor:pointer;padding:4px}
.hamburger span{width:24px;height:2px;background:var(--text);border-radius:2px}
.mobile-menu{display:none;position:fixed;inset:0;background:var(--bg);z-index:99;flex-direction:column;align-items:center;justify-content:center;gap:2rem}
.mobile-menu.open{display:flex}
.mobile-close{position:absolute;top:1.5rem;right:1.5rem;font-size:2rem;cursor:pointer;color:var(--muted)}
.mobile-menu a{font-family:'Syne',sans-serif;font-size:2rem;font-weight:700}
.hero{min-height:100vh;display:flex;align-items:center;padding-top:5rem;position:relative;overflow:hidden}
.hero-glow{position:absolute;top:-20%;left:50%;transform:translateX(-50%);width:900px;height:900px;background:radial-gradient(circle,rgba(122,184,0,.08) 0%,transparent 65%);pointer-events:none}
.hero-glow2{position:absolute;bottom:-30%;right:-10%;width:600px;height:600px;background:radial-gradient(circle,rgba(0,168,130,.06) 0%,transparent 65%);pointer-events:none}
.hero-content{display:grid;grid-template-columns:1fr 1fr;gap:4rem;align-items:center}
.hero-eyebrow{display:inline-flex;align-items:center;gap:.5rem;background:rgba(122,184,0,.12);border:1px solid rgba(122,184,0,.3);color:var(--accent);font-size:.8rem;font-weight:600;letter-spacing:.1em;text-transform:uppercase;padding:.4rem 1rem;border-radius:50px;margin-bottom:1.5rem}
.hero-eyebrow::before{content:'';width:6px;height:6px;background:var(--accent);border-radius:50%;animation:pulse 2s infinite;flex-shrink:0}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
.hero h1{font-size:clamp(2.8rem,5.5vw,5rem);margin-bottom:1.5rem;line-height:1.05}
.hero h1 em{color:var(--accent);font-style:normal}
.hero-sub{color:var(--muted);font-size:1.1rem;line-height:1.7;margin-bottom:2.5rem;max-width:480px}
.hero-actions{display:flex;gap:1rem;flex-wrap:wrap}
.btn-primary{background:var(--accent);color:#000;padding:.85rem 2rem;border-radius:50px;font-weight:700;font-family:'Syne',sans-serif;font-size:.95rem;transition:.2s;border:none;cursor:pointer;display:inline-flex;align-items:center;gap:.5rem;text-decoration:none;white-space:nowrap}
.btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 30px rgba(122,184,0,.3)}
.btn-secondary{background:transparent;color:var(--text);padding:.85rem 2rem;border-radius:50px;font-weight:600;font-size:.95rem;border:1px solid var(--border);transition:.2s;cursor:pointer;display:inline-flex;align-items:center;gap:.5rem;text-decoration:none;white-space:nowrap}
.btn-secondary:hover{border-color:var(--accent);color:var(--accent)}
.hero-image{position:relative}
.hero-image img{width:100%;max-width:520px;border-radius:24px}
.hero-badge{position:absolute;background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:.75rem 1rem;display:flex;align-items:center;gap:.75rem}
.hero-badge.b1{top:10%;left:-5%;animation:float 4s ease-in-out infinite}
.hero-badge.b2{bottom:15%;right:-5%;animation:float 4s ease-in-out infinite .8s}
.badge-icon{width:36px;height:36px;border-radius:8px;background:rgba(122,184,0,.15);display:flex;align-items:center;justify-content:center;font-size:1.2rem;flex-shrink:0}
.badge-text{font-size:.8rem;color:var(--muted)}.badge-val{font-weight:700;font-size:.95rem;font-family:'Syne',sans-serif}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
.trust-bar{background:var(--surface);border-top:1px solid var(--border);border-bottom:1px solid var(--border);padding:1.5rem 0;overflow:hidden}
.trust-items{display:flex;justify-content:center;align-items:center;gap:1.5rem;flex-wrap:wrap}
.trust-item{display:flex;align-items:center;gap:.6rem;font-size:.875rem;color:var(--muted);font-weight:500}
.trust-item .ti{color:var(--accent)}
.products-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:2rem;margin-top:3rem}
.product-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);overflow:hidden;transition:.3s;position:relative;min-width:0}
.product-card:hover{border-color:rgba(122,184,0,.3);transform:translateY(-4px);box-shadow:0 20px 60px rgba(0,0,0,.1)}
.product-card-badge{position:absolute;top:1rem;right:1rem;background:var(--accent);color:#000;font-size:.7rem;font-weight:700;padding:.3rem .8rem;border-radius:50px;font-family:'Syne',sans-serif;z-index:2}
.product-img{width:100%;height:280px;object-fit:contain;background:var(--surface);display:block}
.product-info{padding:1.5rem}
.product-name{font-family:'Syne',sans-serif;font-weight:700;font-size:1.1rem;margin-bottom:.5rem;line-height:1.3}
.product-spec{font-size:.85rem;color:var(--muted);margin-bottom:1rem;line-height:1.6}
.product-price{font-family:'Syne',sans-serif;font-size:1.8rem;font-weight:800;color:var(--accent);margin-bottom:.25rem}
.product-tax{font-size:.75rem;color:var(--muted)}
.product-cta-row{display:flex;align-items:center;justify-content:space-between;margin-top:1.25rem;gap:.75rem;flex-wrap:wrap}
.tag-row{display:flex;flex-wrap:wrap;gap:.4rem;margin-bottom:1rem}
.tag{font-size:.7rem;padding:.25rem .7rem;border-radius:50px;border:1px solid var(--border);color:var(--muted);font-weight:500;white-space:nowrap}
.tag.green{border-color:rgba(0,168,130,.3);color:var(--accent2)}
.features-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem;margin-top:3rem}
.feature-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:2rem;position:relative;overflow:hidden;transition:.3s}
.feature-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,var(--accent),transparent);opacity:0;transition:.3s}
.feature-card:hover::before{opacity:1}
.feature-icon{font-size:2rem;margin-bottom:1rem}
.feature-card h3{font-family:'Syne',sans-serif;font-size:1.1rem;font-weight:700;margin-bottom:.75rem}
.feature-card p{font-size:.9rem;color:var(--muted);line-height:1.7}
.steps{display:grid;grid-template-columns:repeat(4,1fr);gap:1.5rem;margin-top:3rem;position:relative}
.steps::before{content:'';position:absolute;top:2rem;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--border),transparent)}
.step{text-align:center;padding:1.5rem}
.step-num{width:4rem;height:4rem;background:var(--surface2);border:2px solid var(--border);border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Syne',sans-serif;font-weight:800;font-size:1.2rem;margin:0 auto 1.25rem;position:relative;z-index:1}
.step-num.active-step{background:rgba(122,184,0,.1);border-color:var(--accent);color:var(--accent)}
.step h4{font-family:'Syne',sans-serif;font-weight:700;margin-bottom:.5rem}
.step p{font-size:.875rem;color:var(--muted);line-height:1.6}
.testimonials-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem;margin-top:3rem}
.testimonial{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:1.75rem;position:relative}
.testimonial::before{content:'"';position:absolute;top:.5rem;right:1.25rem;font-size:5rem;font-family:'Syne',sans-serif;color:var(--accent);opacity:.15;line-height:1}
.stars{color:#ffd700;font-size:1rem;margin-bottom:.75rem;letter-spacing:.1em}
.t-text{font-size:.9rem;color:var(--muted);line-height:1.7;margin-bottom:1.25rem}
.t-author{display:flex;align-items:center;gap:.75rem}
.t-avatar{width:40px;height:40px;border-radius:50%;background:var(--surface2);display:flex;align-items:center;justify-content:center;font-weight:700;font-family:'Syne',sans-serif;font-size:.9rem;flex-shrink:0}
.t-name{font-weight:600;font-size:.875rem}.t-loc{font-size:.75rem;color:var(--muted)}
.faq-list{max-width:780px;margin:3rem auto 0}
.faq-item{border-bottom:1px solid var(--border);overflow:hidden}
.faq-q{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 0;cursor:pointer;font-weight:600;font-family:'Syne',sans-serif;font-size:1rem;gap:1rem;transition:.2s}
.faq-q:hover{color:var(--accent)}
.faq-arrow{font-size:1.2rem;transition:.3s;color:var(--muted);flex-shrink:0}
.faq-item.open .faq-arrow{transform:rotate(45deg);color:var(--accent)}
.faq-a{max-height:0;overflow:hidden;transition:max-height .4s ease,padding .3s}
.faq-item.open .faq-a{max-height:300px;padding-bottom:1.25rem}
.faq-a p{font-size:.9rem;color:var(--muted);line-height:1.8}
.banner{background:linear-gradient(135deg,rgba(122,184,0,.12),rgba(0,168,130,.08));border:1px solid rgba(122,184,0,.2);border-radius:24px;padding:4rem;text-align:center;position:relative;overflow:hidden;margin:2rem 0}
.banner::before{content:'';position:absolute;inset:-50%;background:radial-gradient(circle at center,rgba(122,184,0,.06),transparent 50%);pointer-events:none}
.banner h2{font-size:clamp(1.8rem,4vw,3rem);margin-bottom:1rem}
.banner p{color:var(--muted);font-size:1.05rem;max-width:500px;margin:0 auto 2rem}
.product-page-grid{display:grid;grid-template-columns:1fr 1fr;gap:4rem;align-items:start;margin-top:2rem}
.product-gallery{position:sticky;top:6rem}
.main-img{width:100%;border-radius:var(--radius);background:var(--surface);overflow:hidden;border:1px solid var(--border);box-shadow:0 4px 24px rgba(0,0,0,.06)}
.main-img img{width:100%;height:clamp(320px,45vw,460px);object-fit:contain;display:block}
.thumb-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:.75rem;margin-top:.75rem}
.thumb{border-radius:var(--radius-sm);background:var(--surface);overflow:hidden;cursor:pointer;border:2px solid transparent;transition:.2s;aspect-ratio:1}
.thumb.active,.thumb:hover{border-color:var(--accent)}
.thumb img{width:100%;height:100%;object-fit:contain;display:block}
.product-detail-price{font-family:'Syne',sans-serif;font-size:3rem;font-weight:800;color:var(--accent);margin:1rem 0 .25rem}
.product-detail-tax{font-size:.85rem;color:var(--muted);margin-bottom:1.5rem}
.product-features-list{list-style:none;margin:1.5rem 0}
.product-features-list li{display:flex;align-items:flex-start;gap:.75rem;padding:.6rem 0;border-bottom:1px solid var(--border);font-size:.9rem;color:var(--muted);line-height:1.5}
.product-features-list li::before{content:'✓';color:var(--accent);font-weight:700;flex-shrink:0;margin-top:.05rem}
.specs-table-wrap{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);overflow:hidden;margin-top:.5rem}
.specs-table{width:100%;border-collapse:collapse;table-layout:fixed}
.specs-table td{padding:.7rem;font-size:.875rem;border-bottom:1px solid var(--border);word-break:break-word}
.specs-table tr:last-child td{border-bottom:none}
.specs-table td:first-child{color:var(--muted);width:45%}
.specs-table td:last-child{font-weight:500}
.qty-row{display:flex;align-items:center;gap:1rem;margin:1.5rem 0}
.qty-btn{width:40px;height:40px;border:1px solid var(--border);background:var(--surface);color:var(--text);border-radius:8px;font-size:1.2rem;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:.2s;flex-shrink:0}
.qty-btn:hover{border-color:var(--accent);color:var(--accent)}
.qty-val{font-family:'Syne',sans-serif;font-weight:700;font-size:1.1rem;min-width:2rem;text-align:center}
.add-to-cart{width:100%;padding:1rem;background:var(--accent);color:#000;border:none;border-radius:50px;font-family:'Syne',sans-serif;font-weight:700;font-size:1rem;cursor:pointer;transition:.2s;margin-bottom:.75rem}
.add-to-cart:hover{transform:translateY(-2px);box-shadow:0 8px 30px rgba(122,184,0,.3)}
.buy-now{width:100%;padding:1rem;background:transparent;color:var(--text);border:1px solid var(--text);border-radius:50px;font-family:'Syne',sans-serif;font-weight:700;font-size:1rem;cursor:pointer;transition:.2s}
.buy-now:hover{border-color:var(--accent);color:var(--accent)}
.guarantee-row{display:flex;justify-content:space-around;flex-wrap:wrap;gap:.5rem;margin:1.5rem 0;padding:1.25rem;background:var(--surface2);border-radius:var(--radius-sm);border:1px solid var(--border)}
.guarantee-item{text-align:center;font-size:.8rem;color:var(--muted)}
.guarantee-item .gi{font-size:1.4rem;margin-bottom:.25rem}
.product-purchase-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:1.5rem;margin-bottom:1.5rem;box-shadow:0 8px 32px rgba(0,0,0,.06)}
.product-meta-row{display:flex;flex-wrap:wrap;align-items:center;gap:.75rem 1.25rem;margin-bottom:1rem}
.product-stock{display:inline-flex;align-items:center;gap:.35rem;font-size:.8rem;font-weight:600;color:var(--accent2)}
.product-stock::before{content:'';width:7px;height:7px;background:var(--accent2);border-radius:50%;box-shadow:0 0 0 3px rgba(0,168,130,.2);flex-shrink:0}
.product-info-block{background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:1rem 1.15rem;margin-bottom:1.5rem;font-size:.875rem;color:var(--muted);line-height:1.65}
.pd-outcome-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:.75rem;margin-bottom:1.25rem}
.pd-outcome{background:linear-gradient(135deg,rgba(122,184,0,.08),rgba(0,168,130,.05));border:1px solid rgba(122,184,0,.18);border-radius:var(--radius-sm);padding:.85rem .95rem;font-size:.8rem;line-height:1.45;color:var(--muted)}
.pd-outcome strong{display:block;font-family:'Syne',sans-serif;font-size:.82rem;color:var(--text);margin-bottom:.2rem}
.pd-why-Marktio{margin:2rem 0;padding:1.35rem 1.5rem;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);box-shadow:0 4px 20px rgba(0,0,0,.04)}
.pd-why-Marktio h3{font-family:'Syne',sans-serif;font-size:1rem;font-weight:700;margin-bottom:1rem}
.pd-why-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem}
.pd-why-item{font-size:.85rem;color:var(--muted);line-height:1.55;padding-left:1.85rem;position:relative}
.pd-why-item::before{content:'✓';position:absolute;left:0;top:0;color:var(--accent);font-weight:800}
.pd-why-item strong{display:block;color:var(--text);font-size:.88rem;margin-bottom:.2rem}
.pd-payment-row{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:.5rem .85rem;margin-top:1rem;padding-top:1rem;border-top:1px solid var(--border);font-size:.72rem;color:var(--muted);text-align:center}
.pd-pay-badge{background:var(--surface2);border:1px solid var(--border);border-radius:6px;padding:.25rem .5rem;font-weight:600}
.pd-cta-hint{font-size:.78rem;color:var(--muted);text-align:center;margin-top:.65rem;line-height:1.5}
.pd-details{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);margin-bottom:.5rem;overflow:hidden}
.pd-details summary{font-family:'Syne',sans-serif;font-weight:600;font-size:.9rem;padding:1rem 1.1rem;cursor:pointer;list-style:none;display:flex;justify-content:space-between;align-items:center;gap:1rem}
.pd-details summary::-webkit-details-marker{display:none}
.pd-details summary::after{content:'+';color:var(--accent);font-weight:800;font-size:1.1rem;flex-shrink:0}
.pd-details[open] summary::after{content:'−'}
.pd-details .pd-details-body{padding:0 1.1rem 1.1rem;font-size:.875rem;color:var(--muted);line-height:1.65}
.pd-final-cta{margin:2.5rem 0;padding:2rem 1.5rem;text-align:center;background:linear-gradient(135deg,rgba(122,184,0,.1),rgba(0,168,130,.07));border:1px solid rgba(122,184,0,.22);border-radius:var(--radius)}
.pd-final-cta h3{font-family:'Syne',sans-serif;font-size:1.25rem;margin-bottom:.5rem}
.pd-final-cta p{font-size:.875rem;color:var(--muted);margin-bottom:1.25rem;max-width:28rem;margin-left:auto;margin-right:auto;line-height:1.6}
.pd-final-cta .add-to-cart,.pd-final-cta .buy-now{max-width:320px;margin:0 auto .65rem}
.cross-sell-card{display:grid;grid-template-columns:140px 1fr auto;gap:1.25rem;align-items:center;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:1.15rem 1.25rem;transition:.25s;text-decoration:none;color:inherit}
.cross-sell-card:hover{border-color:rgba(122,184,0,.4);box-shadow:0 16px 48px rgba(0,0,0,.1);transform:translateY(-2px)}
.cross-sell-card img{width:100%;height:96px;object-fit:contain;background:var(--surface2);border-radius:var(--radius-sm);border:1px solid var(--border)}
.cross-sell-info h4{font-family:'Syne',sans-serif;font-size:1rem;font-weight:700;margin-bottom:.25rem}
.cross-sell-info p{font-size:.8rem;color:var(--muted);line-height:1.5;margin:0}
.cross-sell-price{font-family:'Syne',sans-serif;font-weight:800;color:var(--accent);font-size:1.15rem;white-space:nowrap}
.cross-sell-arrow{font-size:1.25rem;color:var(--accent);margin-left:.5rem}
.pd-trust-micro{display:flex;flex-wrap:wrap;gap:.65rem 1.25rem;margin-bottom:1rem;font-size:.8rem;color:var(--muted)}
.pd-trust-micro span{display:inline-flex;align-items:center;gap:.35rem}
.pd-trust-micro span::before{content:'';width:5px;height:5px;border-radius:50%;background:var(--accent);flex-shrink:0}
.product-cross-sell{margin-top:4rem;padding-top:3rem;border-top:1px solid var(--border)}
.product-cross-sell h3{font-family:'Syne',sans-serif;font-size:1.2rem;font-weight:700;margin-bottom:.35rem}
.product-cross-sell > p{font-size:.875rem;color:var(--muted);margin-bottom:1.25rem}
.cart-sidebar{position:fixed;top:0;right:-420px;width:420px;height:100vh;background:var(--surface);border-left:1px solid var(--border);z-index:200;transition:.4s cubic-bezier(.4,0,.2,1);padding:1.5rem;overflow-y:auto;display:flex;flex-direction:column}
.cart-sidebar.open{right:0;box-shadow:-8px 0 40px rgba(0,0,0,.12)}
.cart-overlay{position:fixed;inset:0;background:rgba(0,0,0,.3);z-index:199;display:none;backdrop-filter:blur(4px)}
.cart-overlay.open{display:block}
.cart-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.5rem}
.cart-header h3{font-family:'Syne',sans-serif;font-weight:700;font-size:1.2rem}
.cart-close{font-size:1.5rem;cursor:pointer;color:var(--muted);transition:.2s}
.cart-close:hover{color:var(--text)}
.cart-items{flex:1}
.cart-item{display:flex;gap:1rem;padding:1rem 0;border-bottom:1px solid var(--border)}
.cart-item-img{width:64px;height:64px;background:var(--bg);border-radius:8px;object-fit:contain;padding:.5rem;flex-shrink:0}
.cart-item-info{flex:1;min-width:0}
.cart-item-name{font-weight:600;font-size:.875rem;margin-bottom:.25rem;line-height:1.3}
.cart-item-price{font-family:'Syne',sans-serif;font-weight:700;color:var(--accent)}
.cart-item-remove{font-size:.8rem;color:var(--danger);cursor:pointer;margin-top:.25rem}
.cart-footer{margin-top:1.5rem;border-top:1px solid var(--border);padding-top:1.5rem}
.cart-total{display:flex;justify-content:space-between;margin-bottom:1.25rem;font-family:'Syne',sans-serif;font-weight:700;font-size:1.1rem}
.cart-total span:last-child{color:var(--accent)}
.cart-checkout-btn{width:100%;padding:1rem;background:var(--accent);color:#000;border:none;border-radius:50px;font-family:'Syne',sans-serif;font-weight:700;font-size:1rem;cursor:pointer;transition:.2s}
.cart-checkout-btn:hover{transform:translateY(-2px);box-shadow:0 8px 30px rgba(122,184,0,.3)}
.empty-cart{text-align:center;padding:3rem 0;color:var(--muted)}
.empty-cart .ec-icon{font-size:3rem;margin-bottom:1rem}

/* ── CHECKOUT ── */
.co-wrap{padding-top:4.5rem;min-height:100vh}
.co-progress{display:flex;align-items:center;max-width:420px;margin:1.25rem 0}
.co-step{display:flex;flex-direction:column;align-items:center;gap:.35rem;flex:1}
.co-step-circle{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Syne',sans-serif;font-weight:800;font-size:.85rem;flex-shrink:0}
.co-step-circle.done{background:var(--accent);color:#000}
.co-step-circle.active{background:var(--accent);color:#000;box-shadow:0 0 0 4px rgba(122,184,0,.2)}
.co-step-circle.pending{background:var(--surface2);color:var(--muted);border:1px solid var(--border)}
.co-step-label{font-size:.68rem;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--muted)}
.co-step-label.active{color:var(--accent)}
.co-step-line{flex:1;height:2px;background:var(--border);margin-bottom:1.1rem}
.co-step-line.done{background:var(--accent)}
.co-trust{display:flex;flex-wrap:wrap;gap:.5rem .85rem;margin:1rem 0 1.5rem;font-size:.78rem;color:var(--muted)}
.co-trust span{display:inline-flex;align-items:center;gap:.3rem;font-weight:500}
.co-layout{display:grid;grid-template-columns:1fr 360px;gap:2rem;align-items:start}
.co-step-head{display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;margin-bottom:1.25rem;padding-bottom:1rem;border-bottom:1px solid var(--border)}
.co-step-head h4{font-family:'Syne',sans-serif;font-size:1.05rem;font-weight:700;margin:0;line-height:1.25}
.co-step-sub{font-size:.8rem;color:var(--muted);margin:.35rem 0 0;line-height:1.45}
.co-step-pill{font-size:.65rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--accent);background:rgba(122,184,0,.12);padding:.35rem .7rem;border-radius:50px;white-space:nowrap;flex-shrink:0}
.co-total-bar{display:flex;justify-content:space-between;align-items:center;margin-bottom:1.25rem;padding:.85rem 1rem;background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius-sm)}
.co-total-bar span{font-size:.8rem;color:var(--muted)}
.co-total-bar strong{font-family:'Syne',sans-serif;font-size:1.12rem;color:var(--accent)}
.co-back-pay{background:none;border:none;font-size:.8rem;font-weight:600;color:var(--muted);cursor:pointer;padding:.25rem 0 .5rem;display:inline-flex;align-items:center;gap:.35rem;font-family:'DM Sans',sans-serif}
.co-back-pay:hover{color:var(--text)}
.co-pay-frame{--sumup-offset:180px;overflow:hidden;border-radius:var(--radius-sm);height:clamp(560px,75vh,820px);position:relative;margin:0;border:1px solid var(--border);width:100%}
.co-pay-frame-inner{width:100%;height:100%;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;scrollbar-width:none;-ms-overflow-style:none}
.co-pay-frame-inner::-webkit-scrollbar{width:0;height:0;display:none}
.co-pay-frame iframe{width:100%;height:calc(100% + var(--sumup-offset));border:none;margin-top:calc(-1 * var(--sumup-offset));display:block;vertical-align:top}
.co-form-foot{display:flex;align-items:center;justify-content:center;gap:.5rem;padding:.75rem 1rem;border-top:1px solid var(--border);font-size:.7rem;color:var(--muted)}
.co-empty-checkout{padding:3rem 1.5rem;text-align:center;max-width:400px;margin:0 auto}
.co-empty-checkout .ec-ico{font-size:3rem;margin-bottom:1rem}
.co-empty-checkout p{color:var(--muted);font-size:.95rem;line-height:1.6;margin-bottom:1.5rem}
.co-form-box{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);overflow:hidden}
.co-form-hdr{display:flex;align-items:center;justify-content:space-between;padding:1rem 1.25rem;border-bottom:1px solid var(--border);flex-wrap:wrap;gap:.5rem}
.co-form-hdr h3{font-family:'Syne',sans-serif;font-weight:700;font-size:1rem}
.co-form-hdr span{font-size:.72rem;color:var(--muted);display:flex;align-items:center;gap:.35rem}
.co-form-body{padding:1.5rem}
.form-group{margin-bottom:1.1rem}
.form-row{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
.form-label{display:block;font-size:.8rem;font-weight:600;color:var(--muted);margin-bottom:.4rem;letter-spacing:.03em}
.form-input{width:100%;padding:.75rem 1rem;background:var(--bg);border:1px solid var(--border);border-radius:var(--radius-sm);font-family:'DM Sans',sans-serif;font-size:.9rem;color:var(--text);transition:.2s;outline:none}
.form-input:focus{border-color:var(--accent);box-shadow:0 0 0 3px rgba(122,184,0,.1)}
.form-input::placeholder{color:var(--muted);opacity:.7}
.form-section-title{font-family:'Syne',sans-serif;font-weight:700;font-size:.9rem;color:var(--text);margin:1.5rem 0 1rem;padding-bottom:.5rem;border-bottom:1px solid var(--border)}
.card-fields-row{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
.sumup-card-wrapper{position:relative}
.sumup-card-wrapper .form-input{padding-right:2.8rem}
.card-type-icon{position:absolute;right:.75rem;top:50%;transform:translateY(-50%);font-size:1.1rem;pointer-events:none}
.co-submit-btn{width:100%;padding:1rem 1.5rem;background:var(--accent);color:#000;border:none;border-radius:50px;font-family:'Syne',sans-serif;font-weight:800;font-size:1rem;cursor:pointer;transition:.2s;display:flex;align-items:center;justify-content:center;gap:.6rem;margin-top:1.5rem}
.co-submit-btn:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 8px 30px rgba(122,184,0,.3)}
.co-submit-btn:disabled{opacity:.6;cursor:not-allowed;transform:none}
.co-submit-btn .spinner{width:18px;height:18px;border:2px solid rgba(0,0,0,.3);border-top-color:#000;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.co-payment-notice{display:flex;align-items:center;gap:.5rem;margin-top:.85rem;font-size:.75rem;color:var(--muted);justify-content:center}
.co-payment-notice svg{flex-shrink:0}
.pay-method-tabs{display:flex;gap:.5rem;margin-bottom:1.25rem;flex-wrap:wrap}
.pay-tab{padding:.5rem 1rem;border-radius:50px;border:1px solid var(--border);font-size:.8rem;font-weight:600;cursor:pointer;transition:.2s;background:var(--surface);color:var(--muted);display:flex;align-items:center;gap:.35rem}
.pay-tab.active{border-color:var(--accent);background:rgba(122,184,0,.08);color:var(--text)}
.pay-tab:hover:not(.active){border-color:rgba(122,184,0,.3)}
.co-success{text-align:center;padding:3rem 2rem}
.co-success .success-icon{font-size:4rem;margin-bottom:1rem}
.co-success h2{font-family:'Syne',sans-serif;font-size:1.6rem;margin-bottom:.75rem}
.co-success p{color:var(--muted);font-size:.95rem;line-height:1.7;max-width:380px;margin:0 auto 1.5rem}
.co-error-msg{background:rgba(224,48,48,.08);border:1px solid rgba(224,48,48,.25);border-radius:var(--radius-sm);padding:.85rem 1rem;font-size:.85rem;color:var(--danger);margin-top:1rem;line-height:1.5}
.co-summary{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:1.5rem;position:sticky;top:5.5rem}
.co-summary h3{font-family:'Syne',sans-serif;font-size:1rem;font-weight:700;margin-bottom:1rem;padding-bottom:.75rem;border-bottom:1px solid var(--border)}
.order-item{display:flex;align-items:center;gap:.85rem;padding:.65rem 0;border-bottom:1px solid var(--border)}
.order-item:last-of-type{border-bottom:none}
.order-item-img{width:52px;height:52px;background:var(--bg);border-radius:8px;object-fit:contain;padding:.4rem;flex-shrink:0;border:1px solid var(--border)}
.order-item-name{font-size:.825rem;font-weight:600;flex:1;line-height:1.3;min-width:0}
.order-item-price{font-family:'Syne',sans-serif;font-weight:700;font-size:.875rem;color:var(--accent);white-space:nowrap}
.order-totals{margin-top:.75rem;padding-top:.75rem;border-top:1px solid var(--border)}
.ot-row{display:flex;justify-content:space-between;font-size:.8rem;padding:.3rem 0;color:var(--muted);gap:.5rem}
.ot-row.total{border-top:1px solid var(--border);margin-top:.5rem;padding-top:.65rem;color:var(--text);font-family:'Syne',sans-serif;font-weight:800;font-size:1.05rem}
.ot-row.total span:last-child{color:var(--accent)}
.co-info-box{margin-top:1rem;padding:.85rem 1rem;background:rgba(122,184,0,.06);border:1px solid rgba(122,184,0,.15);border-radius:var(--radius-sm);font-size:.75rem;color:var(--muted);line-height:1.8}
.co-legal{margin-top:1.5rem;font-size:.72rem;color:var(--muted);text-align:center;line-height:1.8}
.co-legal a{color:var(--accent)}
.policy-content{max-width:760px;margin:0 auto;padding:8rem 1.5rem 4rem;word-break:break-word}
.policy-content h1{font-size:2rem;margin-bottom:.5rem;font-family:'Syne',sans-serif}
.policy-date{font-size:.85rem;color:var(--muted);margin-bottom:3rem}
.policy-content h2{font-family:'Syne',sans-serif;font-size:1.2rem;margin:2rem 0 .75rem}
.policy-content p,.policy-content li{font-size:.95rem;color:var(--muted);line-height:1.85;margin-bottom:.75rem}
.policy-content ul{padding-left:1.25rem;margin-bottom:.75rem}
.policy-content a{color:var(--accent);text-decoration:underline;word-break:break-all}
.policy-content table{width:100%;border-collapse:collapse;margin:1rem 0}
.policy-content table td,.policy-content table th{padding:.75rem;border:1px solid var(--border);font-size:.875rem;text-align:left}
.policy-content table th{background:var(--surface);font-family:'Syne',sans-serif;font-weight:600;color:var(--text)}
footer{background:var(--surface);border-top:1px solid var(--border);padding:4rem 0 2rem}
.footer-grid{display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:3rem;margin-bottom:3rem}
.footer-brand p{font-size:.9rem;color:var(--muted);line-height:1.7;margin-top:1rem;max-width:280px}
.footer-col h5{font-family:'Syne',sans-serif;font-weight:700;font-size:.875rem;margin-bottom:1rem;color:var(--text)}
.footer-col ul{list-style:none}
.footer-col ul li{margin-bottom:.6rem}
.footer-col ul li a{font-size:.875rem;color:var(--muted);transition:.2s}
.footer-col ul li a:hover{color:var(--accent)}
.footer-bottom{border-top:1px solid var(--border);padding-top:2rem;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:1rem}
.footer-bottom p{font-size:.8rem;color:var(--muted)}
.payment-icons{display:flex;gap:.75rem;align-items:center;flex-wrap:wrap}
.payment-icon{background:var(--surface2);border:1px solid var(--border);border-radius:6px;padding:.3rem .6rem;font-size:.7rem;font-weight:700;color:var(--muted)}
.breadcrumb{font-size:.85rem;color:var(--muted);display:flex;align-items:center;gap:.5rem;padding:5rem 0 1rem;flex-wrap:wrap}
.breadcrumb a{color:var(--muted);transition:.2s}
.breadcrumb a:hover{color:var(--accent)}
.cart-btn{position:relative;background:var(--surface);border:1px solid var(--border);padding:.55rem .9rem;border-radius:50px;cursor:pointer;display:flex;align-items:center;gap:.4rem;font-size:.875rem;transition:.2s;color:var(--text);flex-shrink:0}
.cart-btn:hover{border-color:var(--accent)}
.cart-count-badge{position:absolute;top:-6px;right:-6px;background:var(--accent);color:#000;width:18px;height:18px;border-radius:50%;font-size:.65rem;font-weight:800;display:flex;align-items:center;justify-content:center}
.section-title{text-align:center;max-width:600px;margin:0 auto}
.section-title h2{font-size:clamp(1.8rem,4vw,3rem);margin-bottom:1rem}
.section-title p{color:var(--muted);font-size:1.05rem;line-height:1.7}
.progress-bar{position:fixed;top:0;left:0;height:2px;background:var(--accent);z-index:1000;transition:width .1s;width:0}
.notif{position:fixed;bottom:2rem;right:2rem;background:var(--surface);border:1px solid var(--accent);border-radius:var(--radius-sm);padding:1rem 1.5rem;display:flex;align-items:center;gap:.75rem;z-index:300;transform:translateY(100px);opacity:0;transition:.4s cubic-bezier(.4,0,.2,1);max-width:calc(100vw - 4rem)}
.notif.show{transform:translateY(0);opacity:1}
.fade-in{opacity:0;transform:translateY(30px);transition:opacity .6s,transform .6s}
.fade-in.visible{opacity:1;transform:translateY(0)}
.delay-1{transition-delay:.1s}.delay-2{transition-delay:.2s}.delay-3{transition-delay:.3s}

/* ─── TABLET (max 1024px) ──────────────────────────────────────────────────── */
@media(max-width:1024px){
  .footer-grid{grid-template-columns:1fr 1fr;gap:2rem}
  .co-layout{grid-template-columns:1fr;gap:1.25rem}
  .co-layout .co-summary-mobile-first{order:-1}
  .co-summary{position:static}

  /* Hide scrollbars on tablet/mobile (keep scrolling) */
  html,body{scrollbar-width:none;-ms-overflow-style:none}
  html::-webkit-scrollbar,body::-webkit-scrollbar{width:0;height:0;display:none}
  *{scrollbar-width:none;-ms-overflow-style:none}
  *::-webkit-scrollbar{width:0;height:0;display:none}
}

/* ─── MOBILE (max 900px) ───────────────────────────────────────────────────── */
@media(max-width:900px){
  section{padding:4rem 0}
  .container{padding:0 1rem}
  .hero-content{grid-template-columns:1fr}
  .hero-image{display:none}
  .hero h1{font-size:clamp(2rem,8vw,3.5rem)}
  .hero-sub{font-size:1rem;max-width:100%}
  .hero-actions{gap:.75rem}
  .btn-primary,.btn-secondary{padding:.75rem 1.5rem;font-size:.875rem}
  .products-grid{grid-template-columns:1fr}
  .features-grid{grid-template-columns:1fr}
  .testimonials-grid{grid-template-columns:1fr}
  .steps{grid-template-columns:repeat(2,1fr)}
  .steps::before{display:none}
  .product-page-grid{grid-template-columns:1fr}
  .product-gallery{position:static}
  .product-detail-price{font-size:2.2rem}
  .pd-outcome-grid{grid-template-columns:1fr 1fr}
  .pd-why-grid{grid-template-columns:1fr}
  .cross-sell-card{grid-template-columns:88px 1fr;gap:.75rem}
  .cross-sell-price{font-size:1rem}
  .cross-sell-arrow{display:none}
  .footer-grid{grid-template-columns:1fr 1fr;gap:1.5rem}
  .nav-links{display:none}
  .hamburger{display:flex}
  .cart-sidebar{width:100%;right:-100%}
  .co-layout{grid-template-columns:1fr;gap:1.25rem}
  .co-layout .co-summary-mobile-first{order:-1}
  .co-summary{position:static;margin-top:0}
  .form-row{grid-template-columns:1fr}
  .card-fields-row{grid-template-columns:1fr}
  .banner{padding:2.5rem 1.25rem;border-radius:16px}
  .notif{bottom:1rem;right:1rem;left:1rem;max-width:none}
  .trust-items{gap:1rem}
  .trust-item{font-size:.8rem}
  .breadcrumb{padding:4rem 0 .75rem;font-size:.8rem}
  .main-img img{height:clamp(240px,55vw,380px)}
  .co-pay-frame{--sumup-offset:120px;height:clamp(520px,72vh,760px)}
}

/* ─── SMALL MOBILE (max 600px) ─────────────────────────────────────────────── */
@media(max-width:600px){
  section{padding:3rem 0}
  .container{padding:0 .875rem}
  .hero{padding-top:4rem}
  .hero h1{font-size:clamp(1.8rem,9vw,2.8rem)}
  .hero-eyebrow{font-size:.7rem;padding:.35rem .75rem}
  .hero-actions{flex-direction:column;align-items:flex-start}
  .btn-primary,.btn-secondary{width:100%;justify-content:center;padding:.85rem 1.5rem}
  .products-grid{gap:1.25rem}
  .product-img{height:220px}
  .product-price{font-size:1.5rem}
  .product-cta-row{flex-direction:column;align-items:stretch}
  .product-cta-row .btn-primary,.product-cta-row .btn-secondary{width:100%;justify-content:center}
  .features-grid{gap:1rem}
  .feature-card{padding:1.5rem}
  .steps{grid-template-columns:1fr}
  .step{padding:1rem}
  .testimonials-grid{gap:1rem}
  .footer-grid{grid-template-columns:1fr;gap:1.5rem}
  .footer-bottom{flex-direction:column;align-items:flex-start;gap:.75rem}
  .footer-bottom p{font-size:.75rem}
  .banner{padding:2rem 1rem;border-radius:12px}
  .product-detail-price{font-size:2rem}
  .pd-outcome-grid{grid-template-columns:1fr}
  .guarantee-row{gap:.5rem}
  .guarantee-item{font-size:.72rem;min-width:60px}
  .guarantee-item .gi{font-size:1.1rem}
  .thumb-grid{grid-template-columns:repeat(4,1fr)}
  .cross-sell-card{grid-template-columns:72px 1fr}
  .cross-sell-card img{height:72px}
  .co-progress{max-width:100%}
  .co-step-label{font-size:.6rem}
  .co-step-circle{width:30px;height:30px;font-size:.75rem}
  .co-trust{font-size:.73rem}
  .co-form-hdr{padding:.875rem 1rem}
  .co-form-body{padding:1rem}
  .co-summary{padding:1.25rem}
  .nav-inner{gap:1rem}
  .logo{font-size:1.2rem}
  .nav-cta{padding:.45rem 1rem;font-size:.8rem}
  .cart-btn{padding:.45rem .75rem;font-size:.8rem}
  .policy-content{padding:6rem 1rem 3rem}
  .policy-content h1{font-size:1.6rem}
  .policy-content table{font-size:.8rem}
  .policy-content table td,.policy-content table th{padding:.5rem}
  .faq-q{font-size:.9rem}
  .section-title h2{font-size:clamp(1.5rem,6vw,2.5rem)}
  .product-page-grid{gap:2rem}
  .product-purchase-card{padding:1.25rem}
  .pd-why-Marktio{padding:1rem 1.15rem}
  .pd-details summary{font-size:.85rem;padding:.875rem 1rem}
  .pd-final-cta{padding:1.5rem 1rem}
  .add-to-cart,.buy-now{font-size:.95rem}
  .specs-table td{padding:.6rem .5rem;font-size:.8rem}
  .co-wrap{padding-top:4rem}
  .co-wrap .breadcrumb{padding:0 0 .75rem}
  .co-pay-frame{--sumup-offset:0px;height:clamp(520px,70vh,740px)}
}

/* ─── EXTRA SMALL (max 380px) ───────────────────────────────────────────────── */
@media(max-width:380px){
  .hero h1{font-size:1.7rem}
  .product-detail-price{font-size:1.75rem}
  .logo{font-size:1.1rem}
  .nav-cta{display:none}
  .guarantee-row{justify-content:flex-start}
  .thumb-grid{grid-template-columns:repeat(3,1fr)}
}
`;




// ─── PRODUCT DATA ────────────────────────────────────────────────────────────
const PRODUCTS = [
  {
    id: 0, sku: "DO-236060",
    name: "Marstek Venus E Gen 3.0 – 5120 Wh",
    price: 349.95,
    href: `/products/${VENUS_HANDLE}`,
    img: "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/51UwKwdlfAL._AC_SL1500.jpg?v=1774702990",
    imgs: [
      "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/51UwKwdlfAL._AC_SL1500.jpg?v=1774702990",
      "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/61BWXog3pOL._AC.jpg?v=1774702991",
      "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/61dxTKuhipL._AC_SL1500.jpg?v=1774702990",
      "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/71tjcZ5uPIL._AC_SL1500.jpg?v=1774702990",
      "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/71TqMgYLnPL._AC_SL1500.jpg?v=1774702991",
    ],
  },
  {
    id: 1, sku: "DO-236061",
    name: "Marstek Solarspeicher B2500D – 2240 Wh",
    price: 157.95,
    href: `/products/${B2500_HANDLE}`,
    img: "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/616a56ETjLL._SL1200.jpg?v=1774702935",
    imgs: [
      "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/616a56ETjLL._SL1200.jpg?v=1774702935",
      "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/61wHQb3un-L._SL1500.jpg?v=1774702935",
      "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/71f2A3EUtUL._SL1500.jpg?v=1774702936",
      "https://cdn.shopify.com/s/files/1/0993/5519/7788/files/71_V7aAzvlL._SL1500.jpg?v=1774702936",
    ],
  },
];

const CART_STORAGE_KEY = "Marktio_cart_v1";

function loadCartFromStorage() {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    const lines = [];
    for (const row of data) {
      if (row == null || typeof row.id !== "number") continue;
      const p = PRODUCTS.find((x) => x.id === row.id);
      if (!p) continue;
      const qty = Number(row.qty);
      lines.push({ ...p, qty: Number.isFinite(qty) && qty >= 1 ? 1 : 1 });
    }
    if (lines.length === 0) return [];
    const firstId = lines[0].id;
    const same = lines.filter((l) => l.id === firstId);
    return same[0] ? [same[0]] : [];
  } catch {
    return [];
  }
}

function cartToStoragePayload(cart) {
  return JSON.stringify(cart.map((i) => ({ id: i.id, qty: i.qty })));
}

// ─── SUMUP CONFIG ─────────────────────────────────────────────────────────────
const SUMUP_API_KEY = "sup_pk_XBqzeJk4fZmkRhUBqSDN3XHAitiuBfLWi";

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmt = (n) => "€" + n.toFixed(2).replace(".", ",");

function luhnCheck(num) {
  const digits = num.replace(/\D/g, "");
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alt) { n *= 2; if (n > 9) n -= 9; }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

function detectCardType(num) {
  const n = num.replace(/\D/g, "");
  if (/^4/.test(n)) return { type: "visa", icon: "💳" };
  if (/^5[1-5]|^2[2-7]/.test(n)) return { type: "mastercard", icon: "💳" };
  if (/^3[47]/.test(n)) return { type: "amex", icon: "💳" };
  return { type: "unknown", icon: "💳" };
}

function formatCardNumber(val) {
  return val.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
}

function formatExpiry(val) {
  const clean = val.replace(/\D/g, "").slice(0, 4);
  if (clean.length >= 3) return clean.slice(0, 2) + "/" + clean.slice(2);
  return clean;
}

// SPA link
function Link({ href, className, style, onClick, children, ...rest }) {
  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) onClick(e);
    const page = pathToPage(href);
    window.history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  return <a href={href} className={className} style={style} onClick={handleClick} {...rest}>{children}</a>;
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer>
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <Link href="/" className="logo">
              <div className="logo-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              Doom<span>Lab</span>
            </Link>
            <p>Ihr Spezialist für intelligente Solarspeicher. Qualitätsprodukte zu fairen Preisen – direkt aus Deutschland.</p>
          </div>
          <div className="footer-col">
            <h5>Produkte</h5>
            <ul>
              <li><Link href={PRODUCTS[0].href}>Marstek Venus E 3.0</Link></li>
              <li><Link href={PRODUCTS[1].href}>Marstek B2500D</Link></li>
              <li><Link href="/products">Alle Produkte</Link></li>
            </ul>
          </div>
          <div className="footer-col">
            <h5>Rechtliches</h5>
            <ul>
              {[
                ["/policies/uber-uns", "Über uns"],
                ["/policies/contact", "Kontakt"],
                ["/policies/impressum", "Impressum"],
                ["/policies/privacy-policy", "Datenschutzrichtlinie"],
                ["/policies/terms-of-service", "Nutzungsbedingungen"],
                ["/policies/refund-policy", "Rückerstattungsrichtlinie"],
                ["/policies/shipping-policy", "Versandrichtlinie"],
              ].map(([href, label]) => (
                <li key={href}><Link href={href}>{label}</Link></li>
              ))}
            </ul>
          </div>
          <div className="footer-col">
            <h5>Kontakt</h5>
            <ul>
              <li><a href="mailto:contact@marktio.shop">contact@marktio.shop</a></li>
              <li><a href="tel:+49421167402">+49 0421 16 74 02</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Marktio.shop – Alle Rechte vorbehalten. Alle Preise inkl. 0% MwSt. (§12 Abs. 3 UStG) für Privatkunden.</p>
          <div className="payment-icons">
            <div className="payment-icon">SUMUP</div>
            <div className="payment-icon">SSL</div>
            <div className="payment-icon">🔒 Sicher</div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
function HomePage({ addToCart }) {
  const [openFaq, setOpenFaq] = useState(null);
  const faqs = [
    ["Warum zahle ich 0% MwSt.?","Seit dem 1. Januar 2023 gilt in Deutschland der Nullsteuersatz (0% MwSt.) für Photovoltaikanlagen und Stromspeicher für Privatkunden. Für Geschäftskunden wird zusätzlich 19% MwSt. fällig."],
    ["Wie lange dauert die Lieferung?","Bearbeitung in der Regel 1–2 Werktage; voraussichtliche Lieferzeit 3–7 Werktage. Kostenloser Versand. Details unter Versand & Lieferung."],
    ["Kann ich den Speicher mit meiner bestehenden PV-Anlage verbinden?","Ja! Die Venus E 3.0 ist AC-gekoppelt und kompatibel mit bestehenden netzgekoppelten Solaranlagen. Der B2500D ist ideal für Balkonkraftwerke."],
    ["Welche Garantie erhalte ich?","Alle Produkte werden mit der gesetzlichen Gewährleistung von 2 Jahren geliefert."],
    ["Kann ich den Speicher zurückgeben?","Ja. Sie können innerhalb von 30 Tagen ab Lieferung widerrufen. Details und Rücksendekosten finden Sie in der Widerrufsbelehrung."],
    ["Wie sicher ist die Bezahlung?","Wir verwenden SumUp als Zahlungsanbieter – PCI-DSS-konform mit SSL-Verschlüsselung."],
  ];
  return (
    <>
      <section className="hero">
        <div className="hero-glow" /><div className="hero-glow2" />
        <div className="container">
          <div className="hero-content">
            <div>
              <div className="hero-eyebrow">🇩🇪 Versand aus Deutschland</div>
              <h1>Solarstrom<br /><em>intelligent</em><br />speichern.</h1>
              <p className="hero-sub">Entdecken Sie unsere Premium-Solarspeicher für Ihr Balkonkraftwerk. Jetzt Energie unabhängig werden – einfach, sicher und rentabel.</p>
              <div className="hero-actions">
                <Link href="/products" className="btn-primary">Produkte entdecken →</Link>
                <button className="btn-secondary" onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}>Wie es funktioniert</button>
              </div>
            </div>
            <div className="hero-image">
              <img src={PRODUCTS[0].img} alt="Marstek Venus E 3.0" style={{ maxWidth: 520, borderRadius: 24, width: "100%" }} />
              <div className="hero-badge b1"><div className="badge-icon">⚡</div><div><div className="badge-val">6000+ Zyklen</div><div className="badge-text">LiFePO₄ Akku</div></div></div>
              <div className="hero-badge b2"><div className="badge-icon">🛡️</div><div><div className="badge-val">IP65 Schutz</div><div className="badge-text">Für drinnen & draußen</div></div></div>
            </div>
          </div>
        </div>
      </section>

      <div className="trust-bar"><div className="container"><div className="trust-items">
        {["✅ 0% MwSt. für Privatkunden","🚚 Versand aus DE · kostenlos","🔒 Sichere Zahlung via SumUp","↩️ 30 Tage Rückgabe","📞 Deutscher Kundendienst"].map(t => <div key={t} className="trust-item"><span className="ti">{t.slice(0,2)}</span>{t.slice(2)}</div>)}
      </div></div></div>

      <section>
        <div className="container">
          <div className="section-title fade-in"><div className="section-label">Unsere Produkte</div><h2>Intelligente Solarspeicher für jede Anforderung</h2><p>Von kompakten Balkonkraftwerk-Speichern bis zur skalierbaren Haushaltslösung.</p></div>
          <div className="products-grid">
            {[
              {p:PRODUCTS[0],badge:"BESTSELLER",badgeColor:"var(--accent)",tags:["LiFePO₄","IP65","5,12 kWh","Notstrom"],spec:"5120 Wh · AC-gekoppelt · Notstrom 2500W · App-gesteuert · bis 15,36 kWh"},
              {p:PRODUCTS[1],badge:"PLUG & PLAY",badgeColor:"var(--accent2)",tags:["LiFePO₄","IP65","2,24 kWh","Erweiterbar"],spec:"2240 Wh · Plug & Play 15 Min. · 6000 Zyklen · bis 8,96 kWh"},
            ].map(({p,badge,badgeColor,tags,spec})=>(
              <div key={p.id} className="product-card">
                <div className="product-card-badge" style={{background:badgeColor,color:"#000"}}>{badge}</div>
                <Link href={p.href}><img className="product-img" src={p.img} alt={p.name} loading="lazy" /></Link>
                <div className="product-info">
                  <div className="tag-row">{tags.map(t=><span key={t} className={`tag${t==="LiFePO₄"||t==="IP65"?" green":""}`}>{t}</span>)}</div>
                  <Link href={p.href}><div className="product-name">{p.name}</div></Link>
                  <div className="product-spec">{spec}</div>
                  <div className="product-price">{fmt(p.price)}</div>
                  <div className="product-tax">inkl. 0% MwSt. (für Privatkunden) · kostenloser Versand (DE)</div>
                  <div className="product-cta-row">
                    <button className="btn-primary" onClick={()=>addToCart(p.id)}>In den Warenkorb</button>
                    <Link href={p.href} className="btn-secondary" style={{padding:".65rem 1.2rem"}}>Details →</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{paddingTop:0}}>
        <div className="container">
          <div className="section-title fade-in"><div className="section-label">Vorteile</div><h2>Warum Marktio?</h2><p>Premium-Hardware, transparente Preise, schneller Versand.</p></div>
          <div className="features-grid">
            {[["🔋","Maximale Lebensdauer","LiFePO₄-Technologie mit über 6000 Ladezyklen sorgt für Jahrzehnte zuverlässiger Energiespeicherung."],["⚡","Notstromversorgung","Bei Stromausfall versorgt der Venus E 3.0 Ihre wichtigen Geräte mit bis zu 2500 W unterbrechungsfrei."],["📱","Smarte App-Steuerung","Überwachen Sie Ladezustand und Solarertrag jederzeit – direkt auf Ihrem Smartphone."],["🧩","Modular erweiterbar","Venus E 3.0 skaliert bis zu 15,36 kWh, B2500D bis 8,96 kWh."],["🌐","Plug & Play kompatibel","Nahtlose Integration in bestehende Solaranlagen. Keine Änderungen am Wechselrichter nötig."],["🛡️","IP65 Schutzklasse","Staub- und spritzwassergeschützt – für Innen- und Außenbereiche gemacht."]].map(([icon,title,desc],i)=>(
              <div key={i} className={`feature-card fade-in delay-${(i%3)+1}`}><div className="feature-icon">{icon}</div><h3>{title}</h3><p>{desc}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works">
        <div className="container">
          <div className="section-title fade-in"><div className="section-label">So einfach geht's</div><h2>In 4 Schritten zur Energieunabhängigkeit</h2></div>
          <div className="steps">
            {[["Produkt wählen","Wählen Sie den passenden Speicher für Ihre Anlage."],["Sicher bestellen","Unkompliziert und sicher bezahlen. Rechnung wird automatisch zugesandt."],["Lieferung aus DE","Versand per Spedition aus Deutschland. Schnell und zuverlässig."],["Einstecken & sparen","Anschließen, App einrichten – sofort mit Sparen beginnen."]].map(([h,p],i)=>(
              <div key={i} className="step fade-in"><div className={`step-num${i===0?" active-step":""}`}>{i+1}</div><h4>{h}</h4><p>{p}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section style={{paddingTop:0}}>
        <div className="container">
          <div className="section-title fade-in"><div className="section-label">Kundenstimmen</div><h2>Was unsere Kunden sagen</h2></div>
          <div className="testimonials-grid">
            {[["MK","Markus K.","München","Die Venus E 3.0 läuft seit 3 Monaten einwandfrei. Die App ist intuitiv und ich sehe genau, wie viel ich spare. Sehr empfehlenswert!"],["SF","Sandra F.","Hamburg","Der B2500D ist perfekt für unser Balkonkraftwerk. In 20 Minuten installiert. Der Preis-Leistungs-Verhältnis ist unschlagbar."],["TW","Thomas W.","Berlin","Sehr schnelle Lieferung, professionelle Verpackung. Notstromfunktion hat beim letzten Stromausfall perfekt funktioniert!"]].map(([av,name,loc,text])=>(
              <div key={av} className="testimonial fade-in"><div className="stars">★★★★★</div><p className="t-text">{text}</p><div className="t-author"><div className="t-avatar">{av}</div><div><div className="t-name">{name}</div><div className="t-loc">{loc}</div></div></div></div>
            ))}
          </div>
        </div>
      </section>

      <section style={{paddingTop:0}}>
        <div className="container">
          <div className="section-title fade-in"><div className="section-label">FAQ</div><h2>Häufige Fragen</h2></div>
          <div className="faq-list">
            {faqs.map(([q,a],i)=>(
              <div key={i} className={`faq-item${openFaq===i?" open":""}`}>
                <div className="faq-q" onClick={()=>setOpenFaq(openFaq===i?null:i)}>{q}<span className="faq-arrow">+</span></div>
                <div className="faq-a"><p>{a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{padding:"2rem 0 6rem"}}>
        <div className="container">
          <div className="banner fade-in">
            <div className="section-label">Bereit zum Sparen?</div>
            <h2>Jetzt Solarstrom intelligent speichern</h2>
            <p>Schließen Sie sich Hunderten zufriedener Kunden an.</p>
            <Link href="/products" className="btn-primary" style={{fontSize:"1rem",padding:"1rem 2.5rem"}}>Produkte entdecken →</Link>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

// ─── PRODUCTS PAGE ────────────────────────────────────────────────────────────
function ProductsPage({ addToCart }) {
  return (
    <div style={{paddingTop:"5rem"}}>
      <div className="container">
        <div className="breadcrumb">
          <Link href="/">Startseite</Link><span>›</span><span style={{color:"var(--text)"}}>Produkte</span>
        </div>
        <div className="section-title" style={{textAlign:"left",maxWidth:"none",margin:"0 0 3rem"}}>
          <div className="section-label">Sortiment</div><h2>Alle Produkte</h2>
          <p style={{color:"var(--muted)"}}>Premium-Solarspeicher für Balkonkraftwerk und Haushalt. 0% MwSt. für Privatkunden.</p>
        </div>
        <div className="products-grid">
          {[
            {p:PRODUCTS[0],badge:"BESTSELLER",bc:"var(--accent)",tags:["LiFePO₄","IP65","5,12 kWh","Notstrom"],spec:"AC-gekoppelt · 2500W · App · 15,36 kWh erweiterbar · SKU: DO-236060"},
            {p:PRODUCTS[1],badge:"PLUG & PLAY",bc:"var(--accent2)",tags:["LiFePO₄","IP65","2,24 kWh","Erweiterbar"],spec:"Plug & Play 15 Min. · 6000 Zyklen · 8,96 kWh erweiterbar · SKU: DO-236061"},
          ].map(({p,badge,bc,tags,spec})=>(
            <div key={p.id} className="product-card">
              <div className="product-card-badge" style={{background:bc,color:"#000"}}>{badge}</div>
              <Link href={p.href}><img className="product-img" src={p.img} alt={p.name} /></Link>
              <div className="product-info">
                <div className="tag-row">{tags.map(t=><span key={t} className={`tag${t==="LiFePO₄"||t==="IP65"?" green":""}`}>{t}</span>)}</div>
                <Link href={p.href}><div className="product-name">{p.name}</div></Link>
                <div className="product-spec">{spec}</div>
                <div className="product-price">{fmt(p.price)}</div>
                <div className="product-tax">inkl. 0% MwSt. · kostenloser Versand (DE) · Lieferung per Spedition</div>
                <div className="product-cta-row">
                  <button className="btn-primary" onClick={()=>addToCart(p.id)}>In den Warenkorb</button>
                  <Link href={p.href} className="btn-secondary" style={{padding:".65rem 1.2rem"}}>Details</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{marginTop:"3rem",padding:"1.5rem",background:"var(--surface)",borderRadius:"var(--radius)",border:"1px solid var(--border)",fontSize:".875rem",color:"var(--muted)",lineHeight:1.7}}>
          <strong style={{color:"var(--text)"}}>Hinweis zu Preisen:</strong> Alle Preise verstehen sich inkl. 0% Mehrwertsteuer gemäß §12 Abs. 3 UStG für Privatpersonen. Für Unternehmer wird zusätzlich 19% Umsatzsteuer berechnet.
        </div>
      </div>
      <div style={{height:"4rem"}} /><Footer />
    </div>
  );
}

// ─── PRODUCT DETAIL PAGE ──────────────────────────────────────────────────────
function ProductDetailPage({ addToCart, productIndex, navigate }) {
  const p = PRODUCTS[productIndex];
  const other = PRODUCTS[1 - productIndex];
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const isVenus = productIndex === 0;

  const specs = isVenus
    ? [["Kapazität","5120 Wh (5,12 kWh)"],["Lade-/Entladeleistung","2500 W"],["Notstromleistung","bis 2500 W"],["Zelltechnologie","LiFePO₄"],["Ladezyklen","&gt; 6000"],["Schutzklasse","IP65"],["Kopplung","AC-gekoppelt"],["Erweiterbar auf","bis 15,36 kWh"],["Steuerung","App (iOS/Android)"],["Artikelnummer","DO-236060"]]
    : [["Kapazität","2240 Wh (2,24 kWh)"],["Max. Eingangsleistung","1600 W"],["Max. Ausgangsleistung","800 W"],["Zelltechnologie","LiFePO₄"],["Ladezyklen","&gt; 6000"],["Schutzklasse","IP65"],["Max. Kapazität (erweiterbar)","bis 8,96 kWh"],["Installation","ca. 15 Minuten"],["Steuerung","App (iOS/Android)"],["Artikelnummer","DO-236061"]];

  const features = isVenus
    ? ["38,5% weniger Volumen als die Vorgängergeneration – kompakt und elegant","Dedizierter Notstromanschluss (Backup) mit bis zu 2500 W Leistung","Intelligente App-Steuerung – unterstützt dynamische Stromtarife","LiFePO₄-Akku mit über 6000 Ladezyklen für maximale Lebensdauer","Modular erweiterbar von 5,12 kWh auf bis zu 15,36 kWh","Kompatibel mit bestehenden netzgekoppelten PV-Systemen","IP65-Schutzklasse – geeignet für Innen- und Außenbereiche","Lieferumfang: 1x Venus E 3.0 Speicher (5120 Wh), 1x AC-Kabel (2 m)"]
    : ["Schnelle Installation in nur 15 Minuten – wirklich Plug & Play","Kapazität 2,24 kWh, mit bis zu 3 Erweiterungsmodulen auf 8,96 kWh erweiterbar","Maximale Eingangsleistung 1600 W, Ausgangsleistung 800 W","LiFePO₄-Zelltechnologie mit über 6000 Zyklen","IP65-Schutzklasse – sicher im Freien einsetzbar","Intuitive App-Steuerung (iOS und Android)","Lieferumfang: 1x B2500D Speicher, 4x MC4-Verlängerungskabel (2 m)","Hinweis: Smart Meter und Wechselrichter nicht im Lieferumfang"];

  const handleAdd = (silent) => {
    addToCart(p.id, 1, silent);
    if (!silent) navigate("/checkout");
  };

  return (
    <div style={{paddingTop:"4rem"}}>
      <div className="container">
        <div className="breadcrumb">
          <Link href="/">Startseite</Link><span>›</span>
          <Link href="/products">Produkte</Link><span>›</span>
          <span style={{color:"var(--text)"}}>{isVenus?"Venus E 3.0":"B2500D"}</span>
        </div>
        <div className="product-page-grid">
          <div className="product-gallery">
            <div className="main-img"><img src={p.imgs[activeImg]} alt={p.name} /></div>
            <div className="thumb-grid">
              {p.imgs.map((src,i)=>(
                <div key={i} className={`thumb${activeImg===i?" active":""}`} onClick={()=>setActiveImg(i)}>
                  <img src={src} alt="" />
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="section-label">Marstek · SKU: {p.sku}</div>
            <h1 style={{fontSize:"clamp(1.4rem,4vw,2.4rem)",marginBottom:".5rem"}}>{isVenus?"Venus E Gen 3.0 – 5120 Wh Solarspeicher":"Solarspeicher B2500D – 2240 Wh (2025)"}</h1>
            <div className="tag-row">
              {(isVenus?["LiFePO₄","IP65","5,12 kWh","Notstrom 2500 W","AC-gekoppelt"]:["LiFePO₄","IP65","2,24 kWh","Plug & Play","Erweiterbar"]).map(t=><span key={t} className={`tag${t==="LiFePO₄"||t==="IP65"?" green":""}`}>{t}</span>)}
            </div>
            <div className="product-meta-row">
              <span style={{color:"#ffd700",letterSpacing:".06em"}}>★★★★★</span>
              <span style={{fontSize:".85rem",color:"var(--muted)"}}>4,9/5 · {isVenus?47:83} Bewertungen</span>
              <span className="product-stock">Auf Lager – schnell versandfertig</span>
            </div>

            <div className="product-purchase-card">
              <div className="product-detail-price">{fmt(p.price)}</div>
              <div className="product-detail-tax">inkl. 0% MwSt. (für Privatkunden gem. §12 Abs. 3 UStG) · kostenloser Versand (DE) · Spedition</div>
              <div className="guarantee-row">
                {[["🚚","Versand aus DE"],["↩️","30 Tage Rückgabe"],["🛡️","2 J. Gewährleistung"],["🔒","Sichere Zahlung"]].map(([gi,label])=>(
                  <div key={label} className="guarantee-item"><div className="gi">{gi}</div>{label}</div>
                ))}
              </div>
              <div className="qty-row">
                <span style={{fontSize:".9rem",color:"var(--muted)"}}>Menge:</span>
                <button disabled className="qty-btn" onClick={()=>setQty(q=>Math.max(1,q-1))}>−</button>
                <span className="qty-val">{qty}</span>
                <button disabled className="qty-btn" onClick={()=>setQty(q=>q+1)}>+</button>
              </div>
              <button className="add-to-cart" onClick={()=>handleAdd(false)}>In den Warenkorb legen →</button>
              <Link href="/checkout" className="buy-now" style={{display:"block",textAlign:"center"}} onClick={()=>handleAdd(true)}>Jetzt sicher zur Kasse</Link>
              <p className="pd-cta-hint">Kein Risiko: gesetzliches Widerrufsrecht · Rechnung nach Versand · Support: <a href="mailto:contact@marktio.shop" style={{color:"var(--accent2)"}}>contact@marktio.shop</a></p>
              <div className="pd-payment-row">
                {["Visa","Mastercard","Amex","u. v. m."].map(b=><span key={b} className="pd-pay-badge">{b}</span>)}
                <span>über SumUp – PCI-DSS konform</span>
              </div>
            </div>

            <div className="pd-trust-micro">
              {["Versand aus Deutschland","30 Tage Widerruf","SSL & sichere Zahlung (SumUp)"].map(s=><span key={s}>{s}</span>)}
            </div>
            <div className="pd-outcome-grid">
              {(isVenus?[["Weniger Netzbezug","Eigenverbrauch optimieren"],["Backup bei Ausfall","Notstrom 2500 W"],["Zukunftssicher","Bis 15,36 kWh erweiterbar"]]:
                        [["Schnell starten","In ca. 15 Min. einsatzbereit"],["Skalierbar","Bis 8,96 kWh erweiterbar"],["Langlebig","LiFePO₄, >6000 Zyklen"]]).map(([h,d])=>(
                <div key={h} className="pd-outcome"><strong>{h}</strong>{d}</div>
              ))}
            </div>

            <div className="product-info-block">
              <strong>Lieferung:</strong> Versand per Spedition aus Deutschland. Tracking und Rechnung erhalten Sie per E-Mail nach Versand.
            </div>

            <div className="pd-why-Marktio">
              <h3>Warum bei Marktio bestellen?</h3>
              <div className="pd-why-grid">
                {[["Faire Preise","0% MwSt. für Privatkunden – keine versteckten Gebühren."],["Support","Versand aus DE, Mo–Fr 9–18 Uhr erreichbar."],["Sicher bezahlen","Zahlung über SumUp, PCI-DSS-konform."]].map(([h,d])=>(
                  <div key={h} className="pd-why-item"><strong>{h}</strong>{d}</div>
                ))}
              </div>
            </div>

            <ul className="product-features-list">
              {features.map((f,i)=><li key={i}>{f}</li>)}
            </ul>

            <h3 style={{fontFamily:"'Syne',sans-serif",fontSize:"1rem",margin:"1.5rem 0 .75rem"}}>Technische Daten</h3>
            <div className="specs-table-wrap">
              <table className="specs-table">
                <tbody>{specs.map(([k,v])=><tr key={k}><td>{k}</td><td dangerouslySetInnerHTML={{__html:v}} /></tr>)}</tbody>
              </table>
            </div>

            <div style={{marginTop:"2rem"}}>
              <h3 style={{fontFamily:"'Syne',sans-serif",fontSize:"1.05rem",fontWeight:700,marginBottom:"1rem"}}>Häufige Fragen</h3>
              {(isVenus
                ? [["Passt der Venus E 3.0 zu meiner bestehenden Solaranlage?","Der Speicher ist AC-gekoppelt und für netzgekoppelte PV-Systeme konzipiert."],["Was ist im Lieferumfang enthalten?","1× Venus E 3.0 (5120 Wh) und 1× AC-Kabel (2 m)."],["Kann ich widerrufen?","Ja — 30 Tage ab Lieferung, siehe Widerrufsbelehrung."]]
                : [["Brauche ich einen speziellen Wechselrichter?","Für maximale Effizienz empfiehlt sich ein Marstek-Wechselrichter."],["Was liegt der Lieferung bei?","1× B2500D und 4× MC4-Verlängerungskabel (2 m)."],["Kann ich widerrufen?","Ja — 30 Tage ab Lieferung, siehe Widerrufsbelehrung."]]
              ).map(([q,a])=>(
                <details key={q} className="pd-details"><summary>{q}</summary><div className="pd-details-body">{a}</div></details>
              ))}
            </div>

            <div className="pd-final-cta">
              <h3>Bereit, Ihren Strom zu speichern?</h3>
              <p>Versand aus DE, gesetzliche Gewährleistung und sichere Zahlung.</p>
              <button className="add-to-cart" onClick={()=>handleAdd(false)}>In den Warenkorb</button>
              <Link href="/checkout" className="buy-now" style={{display:"block",textAlign:"center"}} onClick={()=>handleAdd(true)}>Jetzt sicher zur Kasse</Link>
            </div>

            <div className="product-cross-sell">
              <h3>{isVenus?"Passend für den Einstieg":"Mehr Leistung & Notstrom"}</h3>
              <p style={{fontSize:".875rem",color:"var(--muted)",marginBottom:"1.25rem"}}>{isVenus?"Kompakter Speicher fürs Balkonkraftwerk.":"Wenn Sie mehr Kapazität und Backup-Strom brauchen."}</p>
              <Link href={other.href} className="cross-sell-card">
                <img src={other.img} alt={other.name} />
                <div className="cross-sell-info"><h4>{other.name.split("–")[0]}</h4><p>{isVenus?"2240 Wh · Plug & Play · bis 8,96 kWh":"5120 Wh · Notstrom 2500 W · bis 15,36 kWh"}</p></div>
                <div style={{display:"flex",alignItems:"center",flexShrink:0}}><div className="cross-sell-price">{fmt(other.price)}</div><span className="cross-sell-arrow">→</span></div>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div style={{height:"4rem"}} /><Footer />
    </div>
  );
}
// ─── SUMUP CHECKOUT FORM ──────────────────────────────────────────────────────
function SumUpCheckoutForm({ cart, checkoutStep, setCheckoutStep }) {
  const [fading, setFading] = useState(false);
  const [errors, setErrors] = useState({});
  const allowConversionRef = useRef(false);


  const frameContainerRef = useRef(null);
const [iframeScale, setIframeScale] = useState(1);
const SUMUP_W = 440; // SumUp's approximate internal min-width

useEffect(() => {
  const el = frameContainerRef.current;
  if (!el) return;
  const ro = new ResizeObserver(([entry]) => {
    const w = entry.contentRect.width;
    setIframeScale(w < SUMUP_W ? w / SUMUP_W : 1);
  });
  ro.observe(el);
  return () => ro.disconnect();
}, []);


  const [form, setForm] = useState({
    street: "", zip: "", city: "", country: "DE", email: "",
  });

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  let SUMUP_PAYMENT_LINK =
    cart.length === 1 && cart[0].qty === 1
      ? SUMUP_LINKS[cart[0].sku]
      : null;
  if (!SUMUP_PAYMENT_LINK) SUMUP_PAYMENT_LINK = "https://pay.sumup.com/b2c/QA58DE7R";

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((err) => ({ ...err, [e.target.name]: undefined }));
  };

  const handleProceed = () => {
    const newErrors = {};
    if (!form.street.trim()) newErrors.street = "Pflichtfeld";
    if (!form.zip.trim()) newErrors.zip = "Pflichtfeld";
    if (!form.city.trim()) newErrors.city = "Pflichtfeld";
    if (!form.email.trim()) newErrors.email = "Pflichtfeld";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) newErrors.email = "Bitte gültige E-Mail";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setFading(true);
    setTimeout(() => {
      setCheckoutStep("payment");
      setFading(false);
    }, 280);
  };

 const hasFiredPixelRef = useRef(false);

const handleIframeLoad = (e) => {
  if (!e.target.src || e.target.src === "about:blank") return;
  if (!allowConversionRef.current) return;
  if (hasFiredPixelRef.current) return; // ← add this
  hasFiredPixelRef.current = true;      // ← and this

  if (typeof window.fbq !== "undefined") {
    window.fbq('track', 'Purchase');
  }
};

  useEffect(() => {
    allowConversionRef.current = checkoutStep === "payment";
  }, [checkoutStep]);

  return (
    <div className="co-form-box">
      <div className="co-form-hdr">
        <h3>{checkoutStep === "address" ? "Fast geschafft" : "Sicher bezahlen"}</h3>
        <span>SumUp · SSL · PCI-DSS</span>
      </div>

      {/* Preload SumUp early (hidden) so the payment step feels instant */}
      {checkoutStep === "address" && (
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: 0, width: 1, height: 1, overflow: "hidden", opacity: 0, pointerEvents: "none" }}>
          <iframe src={SUMUP_PAYMENT_LINK} title="SumUp Zahlung (Preload)" allow="payment" onLoad={handleIframeLoad} />
        </div>
      )}

      {checkoutStep === "address" && (
        <div className="co-form-body" style={{ opacity: fading ? 0 : 1, transition: "opacity .28s ease" }}>
          <div className="co-step-head">
            <div>
              <h4>Lieferadresse</h4>
              <p className="co-step-sub">Wir nutzen diese Angaben für Versand und Statusmails.</p>
            </div>
            <span className="co-step-pill">Schritt 1 / 2</span>
          </div>

          <div className="co-total-bar">
            <span>Zu zahlen</span>
            <strong>{fmt(subtotal)}</strong>
          </div>

          <Field label="Straße & Hausnummer" name="street" placeholder="Musterstraße 12"
            value={form.street} onChange={handleChange} error={errors.street} />
          <div className="form-row">
            <Field label="PLZ" name="zip" placeholder="10115"
              value={form.zip} onChange={handleChange} error={errors.zip} />
            <Field label="Stadt" name="city" placeholder="Berlin"
              value={form.city} onChange={handleChange} error={errors.city} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="co-country">Land</label>
            <select id="co-country" name="country" value={form.country} onChange={handleChange} className="form-input" style={{ cursor: "pointer" }}>
              <option value="DE">Deutschland</option>
            </select>
          </div>
          <Field label="E-Mail für Bestellbestätigung" name="email" type="email" placeholder="max@beispiel.de"
            value={form.email} onChange={handleChange} error={errors.email} />

          <button type="button" className="co-submit-btn" onClick={handleProceed} style={{ marginTop: "0.25rem" }}>
            Weiter zur Zahlung
          </button>
          <p className="co-payment-notice" style={{ marginTop: "0.65rem" }}>
            <span style={{ fontSize: "1rem" }}>🔒</span>
            Sie werden gleich zu SumUp weitergeleitet — Karte oder Apple/Google Pay.
          </p>
        </div>
      )}

      {checkoutStep === "payment" && (
        <div className="co-form-body" style={{ paddingTop: "1rem" }}>
          <button type="button" className="co-back-pay" onClick={() => setCheckoutStep("address")}>
            ← Adresse bearbeiten
          </button>
          <div className="co-step-head" style={{ marginBottom: "1rem" }}>
            <div>
              <h4>Zahlung abschließen</h4>
              <p className="co-step-sub">Geben Sie Ihre Daten bei SumUp ein — wir sehen Ihre Kartendaten nicht.</p>
            </div>
            <span className="co-step-pill">Schritt 2 / 2</span>
          </div>
        <div className="co-pay-frame" ref={frameContainerRef}>
  <div className="co-pay-frame-inner">
    <iframe
      src={SUMUP_PAYMENT_LINK}
      title="SumUp Zahlung"
      allow="payment"
      onLoad={handleIframeLoad}
      style={{
        width: iframeScale < 1 ? `${SUMUP_W}px` : "100%",
        border: "none",
        display: "block",
        transform: `scale(${iframeScale})`,
        transformOrigin: "top left",
      }}
    />
  </div>
</div>
        </div>
      )}

      <div className="co-form-foot">
        🔒 256-bit SSL · Keine Kartendaten bei Marktio gespeichert
      </div>
    </div>
  );
}

function Field({ label, name, placeholder, value, onChange, type = "text", style = {}, error }) {
  return (
    <div className="form-group" style={style}>
      <label className="form-label" htmlFor={`co-field-${name}`}>{label}</label>
      <input
        id={`co-field-${name}`}
        name={name} type={type} placeholder={placeholder} value={value} onChange={onChange}
        className="form-input"
        style={error ? { borderColor: "var(--danger)" } : undefined}
        autoComplete={name === "email" ? "email" : name === "street" ? "street-address" : name === "zip" ? "postal-code" : name === "city" ? "address-level2" : undefined}
      />
      {error && (
        <span style={{ fontSize: ".72rem", color: "var(--danger)", marginTop: 6, display: "block" }}>
          {error}
        </span>
      )}
    </div>
  );
}

// ─── CHECKOUT PAGE ────────────────────────────────────────────────────────────
function CheckoutPage({ cart }) {
  const [success, setSuccess] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState("address");
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  useEffect(() => {
    if (cart.length === 0) setCheckoutStep("address");
  }, [cart.length]);

  if (success) {
    return (
      <div className="co-wrap">
        <div className="container">
          <div className="co-form-box" style={{maxWidth:560,margin:"2rem auto"}}>
            <div className="co-success">
              <div className="success-icon">🎉</div>
              <h2>Vielen Dank für Ihre Bestellung!</h2>
              <p>Eine Bestätigung wurde an <strong>{success.email}</strong> gesendet. Ihre Referenznummer lautet: <strong>{success.checkoutRef}</strong>. Wir melden uns sobald Ihre Bestellung versandfertig ist.</p>
              <Link href="/" className="btn-primary">Zurück zur Startseite</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="co-wrap">
      <div className="container">
        <div className="breadcrumb" style={{paddingTop:0,paddingBottom:".75rem"}}>
          <Link href="/">Startseite</Link><span>›</span>
          <Link href="/products">Produkte</Link><span>›</span>
          <span style={{color:"var(--text)"}}>Kasse</span>
        </div>
        <div className="co-progress">
          <div className="co-step">
            <div className="co-step-circle done">✓</div>
            <div className="co-step-label">Warenkorb</div>
          </div>
          <div className="co-step-line done" />
          <div className="co-step">
            <div className={`co-step-circle ${checkoutStep === "address" ? "active" : "done"}`}>
              {checkoutStep === "payment" ? "✓" : "2"}
            </div>
            <div className={`co-step-label ${checkoutStep === "address" ? "active" : ""}`}>Lieferung</div>
          </div>
          <div className={`co-step-line ${checkoutStep === "payment" ? "done" : ""}`} />
          <div className="co-step">
            <div className={`co-step-circle ${checkoutStep === "payment" ? "active" : "pending"}`}>3</div>
            <div className={`co-step-label ${checkoutStep === "payment" ? "active" : ""}`}>Zahlung</div>
          </div>
        </div>
        <div className="co-trust">
          {["SSL & PCI-DSS (SumUp)","30 Tage Widerruf","Versand kostenlos (DE)"].map(t=><span key={t}>{t}</span>)}
        </div>

        {cart.length === 0 ? (
          <div className="co-empty-checkout">
            <div className="ec-ico">🛒</div>
            <p>Ihr Warenkorb ist leer. Speicher auswählen — die Kasse dauert nur einen Moment.</p>
            <Link href="/products" className="btn-primary" style={{ display: "inline-flex" }}>Zum Shop</Link>
          </div>
        ) : (
          <div className="co-layout">
            <div>
              <SumUpCheckoutForm cart={cart} checkoutStep={checkoutStep} setCheckoutStep={setCheckoutStep} />
              <div className="co-legal">
                Mit Ihrer Bestellung erklären Sie sich mit unseren <Link href="/policies/terms-of-service">Nutzungsbedingungen</Link> und der <Link href="/policies/privacy-policy">Datenschutzrichtlinie</Link> einverstanden. <Link href="/policies/refund-policy">Rückerstattungsrichtlinie</Link>.
              </div>
            </div>
            <div className="co-summary co-summary-mobile-first">
              <h3>Ihre Bestellung</h3>
              {cart.map((item,i)=>(
                <div key={i} className="order-item">
                  <img className="order-item-img" src={item.img} alt={item.name} />
                  <div className="order-item-name">{item.name}<br /><span style={{color:"var(--muted)",fontSize:".72rem"}}>Menge: {item.qty} · {item.sku}</span></div>
                  <div className="order-item-price">{fmt(item.price * item.qty)}</div>
                </div>
              ))}
              <div className="order-totals">
                <div className="ot-row"><span>Zwischensumme</span><span>{fmt(subtotal)}</span></div>
                <div className="ot-row"><span>MwSt.</span><span>0% (§12 Abs. 3 UStG)</span></div>
                <div className="ot-row"><span>Versand</span><span style={{textAlign:"right"}}>kostenlos (DE)</span></div>
                <div className="ot-row total"><span>Gesamt</span><span>{fmt(subtotal)}</span></div>
              </div>
              <div className="co-info-box">
                ✅ <strong style={{color:"var(--text)"}}>0% MwSt.</strong> gem. §12 Abs. 3 UStG<br />
                🚚 Lieferung per Spedition aus DE<br />
                📄 Rechnung automatisch nach Versand<br />
                ↩️ 30 Tage Widerrufsrecht (siehe Widerrufsbelehrung)
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── POLICY PAGES ─────────────────────────────────────────────────────────────
function PolicyPage({ page }) {
  const back = <Link href="/" style={{display:"inline-block",marginTop:"2rem"}}>← Zurück zur Startseite</Link>;
  const pages = {
    impressum: (
      <div className="policy-content">
        <div className="section-label">Rechtliches</div><h1>Impressum</h1><div className="policy-date">Angaben gemäß § 5 TMG</div>
        <h2>Anbieter</h2><p><strong>Marktio</strong><br />Inhaber: Winston Albert Frame<br />Kleine Alexanderstraße 10<br />10178 Berlin, Deutschland</p>
        <h2>Kontakt</h2><p>E-Mail: <a href="mailto:contact@marktio.shop">contact@marktio.shop</a><br />Telefon: +49 0421 16 74 02<br />Geschäftszeiten: Mo–Fr 9:00–18:00 Uhr</p>
        <h2>Handelsübliche Angaben (Transparenz / Google Merchant Center)</h2>
        <p>Währung: Euro (EUR). Warenbeschaffung: <strong>Eigenlager</strong> (Versand aus Deutschland). Liefer- und Rückgabebedingungen finden Sie unter <Link href="/policies/shipping-policy">Versandrichtlinie</Link>, <Link href="/policies/refund-policy">Rückerstattungsrichtlinie</Link> und <Link href="/policies/terms-of-service">Nutzungsbedingungen</Link>.</p>
        <h2>Umsatzsteuer</h2><p>Umsätze für PV-Anlagen und Stromspeicher unterliegen dem Nullsteuersatz gemäß §12 Abs. 3 UStG.</p>
        <h2>Streitschlichtung</h2><p>OS-Plattform: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr</a>. Wir nehmen nicht an Streitbeilegungsverfahren teil.</p>
        {back}
      </div>
    ),
    contact: (
      <div className="policy-content">
        <div className="section-label">Kontakt</div><h1>Kontakt</h1><div className="policy-date">Wir helfen gerne weiter</div>
        <p><strong>Marktio</strong><br />Inhaber: Winston Albert Frame<br />Kleine Alexanderstraße 10<br />10178 Berlin, Deutschland</p>
        <h2>So erreichen Sie uns</h2>
        <p>E-Mail: <a href="mailto:contact@marktio.shop">contact@marktio.shop</a><br />Telefon: <a href="tel:+49421167402">+49 0421 16 74 02</a><br />Geschäftszeiten: Mo–Fr 9:00–18:00 Uhr</p>
        <p>Für rechtliche Angaben siehe <Link href="/policies/impressum">Impressum</Link>.</p>
        {back}
      </div>
    ),
    "privacy-policy": (
      <div className="policy-content">
        <div className="section-label">Rechtliches</div><h1>Datenschutzrichtlinie</h1><div className="policy-date">Stand: April 2026</div>
        <p>Diese Datenschutzrichtlinie erläutert, welche personenbezogenen Daten wir verarbeiten, zu welchen Zwecken und welche Rechte Sie nach der DSGVO haben.</p>

        <h2>1. Verantwortlicher</h2>
        <p><strong>Marktio</strong> (Inhaber: Winston Albert Frame)<br />Kleine Alexanderstraße 10, 10178 Berlin, Deutschland<br />Telefon: +49 0421 16 74 02<br />E-Mail: <a href="mailto:contact@marktio.shop">contact@marktio.shop</a></p>

        <h2>2. Kategorien personenbezogener Daten</h2>
        <ul>
          <li><strong>Bestelldaten:</strong> gekaufte Produkte, Menge, Preis, Rechnungs-/Transaktionsdaten.</li>
          <li><strong>Kontakt- &amp; Lieferdaten:</strong> Name, Adresse, E-Mail, Telefonnummer (sofern angegeben).</li>
          <li><strong>Zahlungsdaten:</strong> werden im Checkout durch Zahlungsdienstleister verarbeitet (wir erhalten i. d. R. keine vollständigen Kartendaten).</li>
          <li><strong>Nutzungs-/Gerätedaten:</strong> technische Logdaten (z. B. IP-Adresse, Zeitstempel, Browser-/Geräteinformationen) zur Bereitstellung und Sicherheit.</li>
          <li><strong>Kommunikationsdaten:</strong> Inhalte von Anfragen per E-Mail/Telefon.</li>
        </ul>

        <h2>3. Zwecke &amp; Rechtsgrundlagen</h2>
        <ul>
          <li><strong>Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO):</strong> Abwicklung von Bestellungen, Lieferung, Rechnungsstellung, Kundenservice.</li>
          <li><strong>Rechtliche Pflichten (Art. 6 Abs. 1 lit. c DSGVO):</strong> steuer- und handelsrechtliche Aufbewahrungspflichten.</li>
          <li><strong>Berechtigte Interessen (Art. 6 Abs. 1 lit. f DSGVO):</strong> Betrieb, IT-Sicherheit, Missbrauchs-/Betrugsprävention, Fehleranalyse.</li>
          <li><strong>Einwilligung (Art. 6 Abs. 1 lit. a DSGVO):</strong> falls wir optionalen Versand von Marketing-Nachrichten anbieten (derzeit nicht standardmäßig).</li>
        </ul>

        <h2>4. Empfänger &amp; Dienstleister</h2>
        <p>Wir setzen Dienstleister ein, die Daten in unserem Auftrag verarbeiten (Art. 28 DSGVO), z. B. für Hosting/IT und Zahlungsabwicklung.</p>
        <ul>
          <li><strong>Zahlungsdienstleister (SumUp):</strong> Zahlungsdaten werden über <strong>SumUp</strong> verarbeitet. Es gilt die <a href="https://sumup.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Datenschutzerklärung von SumUp</a> sowie die des jeweiligen Kartenausstellers bzw. Wallet-Anbieters.</li>
          <li><strong>Versand/Spedition:</strong> Weitergabe von Lieferdaten zur Zustellung (Name, Adresse, ggf. Telefonnummer zur Terminabstimmung).</li>
        </ul>

        <h2>5. Datenübermittlung in Drittländer</h2>
        <p>Soweit Dienstleister oder Unterauftragsverarbeiter außerhalb des EWR eingesetzt werden, erfolgt dies nur bei Vorliegen geeigneter Garantien (z. B. EU-Standardvertragsklauseln) bzw. eines Angemessenheitsbeschlusses.</p>

        <h2>6. Speicherdauer</h2>
        <p>Wir speichern personenbezogene Daten nur so lange, wie es für die genannten Zwecke erforderlich ist. Bestell- und Abrechnungsdaten werden gemäß gesetzlichen Aufbewahrungsfristen (typischerweise 6–10 Jahre) gespeichert.</p>

        <h2>7. Ihre Rechte</h2>
        <ul>
          <li><strong>Auskunft</strong> (Art. 15 DSGVO)</li>
          <li><strong>Berichtigung</strong> (Art. 16 DSGVO)</li>
          <li><strong>Löschung</strong> (Art. 17 DSGVO)</li>
          <li><strong>Einschränkung</strong> (Art. 18 DSGVO)</li>
          <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO)</li>
          <li><strong>Widerspruch</strong> (Art. 21 DSGVO), insbesondere gegen Verarbeitung auf Basis berechtigter Interessen</li>
          <li><strong>Widerruf von Einwilligungen</strong> (Art. 7 Abs. 3 DSGVO) mit Wirkung für die Zukunft</li>
        </ul>
        <p>Kontakt zur Ausübung Ihrer Rechte: <a href="mailto:contact@marktio.shop">contact@marktio.shop</a></p>

        <h2>8. Beschwerderecht</h2>
        <p>Sie haben das Recht, sich bei einer Aufsichtsbehörde zu beschweren (Art. 77 DSGVO).</p>

        <h2>9. Sicherheit</h2>
        <p>Wir treffen angemessene technische und organisatorische Maßnahmen, um Ihre Daten zu schützen (z. B. TLS/SSL-Transportverschlüsselung, Zugriffsbeschränkungen).</p>

        <h2>10. Änderungen</h2>
        <p>Wir können diese Datenschutzrichtlinie aktualisieren, um rechtliche, technische oder organisatorische Änderungen abzubilden.</p>
        {back}
      </div>
    ),
    "terms-of-service": (
      <div className="policy-content">
        <div className="section-label">Rechtliches</div><h1>Nutzungsbedingungen</h1><div className="policy-date">Stand: April 2026</div>
        <p>Diese Nutzungsbedingungen regeln die Nutzung unseres Online-Shops sowie die Bedingungen für Bestellungen. Verbraucherrechte nach zwingendem Recht bleiben unberührt.</p>

        <h2>§ 1 Anbieter &amp; Geltungsbereich</h2>
        <p><strong>Marktio</strong> (Inhaber: Winston Albert Frame), Kleine Alexanderstraße 10, 10178 Berlin, Deutschland, E-Mail: <a href="mailto:contact@marktio.shop">contact@marktio.shop</a>, Telefon: +49 0421 16 74 02. Diese Bedingungen gelten für alle Bestellungen über Marktio.shop.</p>

        <h2>§ 2 Nutzung der Website</h2>
        <ul>
          <li><strong>Zulässige Nutzung:</strong> Sie dürfen die Website nur im Rahmen der geltenden Gesetze nutzen.</li>
          <li><strong>Missbrauch:</strong> Unzulässig sind u. a. Störungen der IT-Systeme, automatisiertes Auslesen (Scraping) ohne Zustimmung oder missbräuchliche Bestellungen.</li>
          <li><strong>Inhalte:</strong> Produktinformationen werden sorgfältig gepflegt; offensichtliche Irrtümer bleiben vorbehalten.</li>
        </ul>

        <h2>§ 3 Vertragsschluss</h2>
        <p>Die Produktdarstellung stellt kein bindendes Angebot dar. Mit Abschluss des Zahlungsvorgangs geben Sie eine verbindliche Bestellung ab. Der Vertrag kommt zustande, wenn wir die Bestellung annehmen (z. B. durch Bestätigung per E-Mail oder Versand der Ware).</p>

        <h2>§ 4 Preise, Währung, Steuern</h2>
        <p>Alle Preise sind in <strong>Euro (EUR)</strong> angegeben. Für Privatpersonen kann der Nullsteuersatz gemäß §12 Abs. 3 UStG gelten, soweit anwendbar. Für Unternehmer kann zusätzlich Umsatzsteuer anfallen.</p>

        <h2>§ 5 Zahlung</h2>
        <p>Die Zahlung erfolgt über <strong>SumUp</strong>. Verfügbare Zahlungsarten können u. a. Visa, Mastercard, American Express, Apple Pay, Google Pay und Shop Pay umfassen (abhängig vom Checkout).</p>

        <h2>§ 6 Versand &amp; Lieferung</h2>
        <p>Informationen zu Lieferzeit, Lieferländern und Versandkosten finden Sie in der <Link href="/policies/shipping-policy">Versandrichtlinie</Link>. Die Lieferung kann per Spedition erfolgen; hierfür können Kontaktdaten zur Terminabstimmung erforderlich sein.</p>

        <h2>§ 7 Rückgabe &amp; Erstattung</h2>
        <p>Regelungen zur Rückgabe (30 Tage) und Rückerstattung finden Sie in der <Link href="/policies/refund-policy">Rückerstattungsrichtlinie</Link>.</p>

        <h2>§ 8 Gewährleistung</h2>
        <p>Es gilt die gesetzliche Gewährleistung von 2 Jahren ab Lieferung der Ware.</p>

        <h2>§ 9 Haftung</h2>
        <p>Wir haften unbeschränkt bei Vorsatz und grober Fahrlässigkeit sowie bei Verletzung von Leben, Körper oder Gesundheit. Bei leichter Fahrlässigkeit haften wir nur bei Verletzung wesentlicher Vertragspflichten (Kardinalpflichten) und beschränkt auf den vorhersehbaren, vertragstypischen Schaden. Die Haftung nach dem Produkthaftungsgesetz bleibt unberührt.</p>

        <h2>§ 10 Eigentumsvorbehalt</h2>
        <p>Bis zur vollständigen Bezahlung bleibt die Ware unser Eigentum.</p>

        <h2>§ 11 Datenschutz</h2>
        <p>Informationen zur Verarbeitung personenbezogener Daten finden Sie in der <Link href="/policies/privacy-policy">Datenschutzrichtlinie</Link>.</p>

        <h2>§ 12 Schlussbestimmungen</h2>
        <p>Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. Gegenüber Verbrauchern bleiben zwingende Verbraucherschutzvorschriften des Staates, in dem der Verbraucher seinen gewöhnlichen Aufenthalt hat, unberührt.</p>
        {back}
      </div>
    ),
    "refund-policy": (
      <div className="policy-content">
        <div className="section-label">Rechtliches</div><h1>Rückerstattungsrichtlinie</h1><div className="policy-date">Stand: April 2026</div>
        <h2>Widerrufsrecht</h2>
        <p>Sie haben das Recht, binnen <strong>30 Tagen</strong> ab dem Tag, an dem Sie oder ein von Ihnen benannter Dritter die Ware in Besitz genommen haben, ohne Angabe von Gründen diesen Vertrag zu widerrufen (Widerrufsfrist). Um das Widerrufsrecht auszuüben, müssen Sie uns</p>
        <p><strong>Marktio</strong>, Inhaber Winston Albert Frame, Kleine Alexanderstraße 10, 10178 Berlin, Deutschland, E-Mail: <a href="mailto:contact@marktio.shop">contact@marktio.shop</a>, Telefon: +49 0421 16 74 02</p>
        <p>mittels einer eindeutigen Erklärung (z. B. E-Mail oder Post) über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren. Sie können dafür das untenstehende Muster-Widerrufsformular verwenden; die Nutzung ist jedoch nicht verpflichtend.</p>
        <h2>Rückgabe &amp; Kosten</h2>
        <p><strong>Rückgabefrist:</strong> 30 Tage ab Lieferung.</p>
        <p><strong>Rücksendekosten:</strong> Kostenlos.</p>
        <h2>Folgen des Widerrufs</h2>
        <p>Wenn Sie diesen Vertrag widerrufen, erstatten wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, nach Eingang und Prüfung der Rücksendung. Die <strong>Bearbeitungszeit</strong> beträgt in der Regel <strong>7 Tage</strong> ab Eingang der Ware bei uns. Für die Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart.</p>
        <p>Sie haben die Ware unverzüglich und in jedem Fall spätestens binnen <strong>30 Tagen</strong> ab dem Tag, an dem Sie uns über den Widerruf informieren, an uns zurückzusenden oder zu übergeben. Die Frist ist gewahrt, wenn Sie die Ware vor Ablauf der Frist von 30 Tagen absenden.</p>
        <h2>Rücksendeadresse &amp; Rücksendekosten</h2>
        <p>Rücksendungen sind an unsere Geschäftsadresse in <strong>Deutschland</strong> zu richten (siehe Anbieteradresse oben); bitte kontaktieren Sie uns vor einer Rücksendung unter <a href="mailto:contact@marktio.shop">contact@marktio.shop</a>, damit wir Sie ggf. zur korrekten Annahme und zum Ablauf beraten können.</p>
        <p><strong>Rücksendekosten:</strong> Wir übernehmen die <strong>Rücksendekosten</strong> (kostenlose Rückgabe).</p>
        <h2>Muster-Widerrufsformular</h2>
        <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",padding:"1.5rem",fontSize:".875rem",color:"var(--muted)"}}>
          An: Marktio, Inhaber Winston Albert Frame, Kleine Alexanderstraße 10, 10178 Berlin, Deutschland, contact@marktio.shop<br /><br />
          Hiermit widerrufe ich den von mir abgeschlossenen Kaufvertrag über die folgende Ware:<br /><br />
          Bestellt am / erhalten am:<br />Bezeichnung der Ware:<br />Name &amp; Anschrift des Verbrauchers:<br />Datum, Unterschrift (nur bei Mitteilung auf Papier):
        </div>
        {back}
      </div>
    ),
    "shipping-policy": (
      <div className="policy-content">
        <div className="section-label">Rechtliches</div><h1>Versandrichtlinie</h1><div className="policy-date">Stand: April 2026</div>
        <p>Diese Angaben dienen der Transparenz gegenüber Kunden und entsprechen den bei Google Merchant Center hinterlegten Informationen zu Marktio.</p>
        <table><thead><tr><th>Merkmal</th><th>Angabe</th></tr></thead><tbody>
          <tr><td>Anbieter</td><td>Marktio, Inhaber Winston Albert Frame, Kleine Alexanderstraße 10, 10178 Berlin, Deutschland · <a href="mailto:contact@marktio.shop">contact@marktio.shop</a> · +49 0421 16 74 02</td></tr>
          <tr><td>Währung</td><td>Euro (EUR)</td></tr>
          <tr><td>Beschaffung / Lager</td><td>Eigenlager</td></tr>
          <tr><td>Versandherkunft</td><td>Deutschland</td></tr>
          <tr><td>Bearbeitungszeit</td><td>1–2 Werktage nach Zahlungseingang</td></tr>
          <tr><td>Lieferzeit</td><td>3–7 Tage</td></tr>
          <tr><td>Versandkosten</td><td>Kostenlos</td></tr>
          <tr><td>Lieferländer</td><td>Deutschland, Vereinigte Staaten</td></tr>
          <tr><td>Rückgabefrist</td><td>30 Tage ab Lieferung (Details: <Link href="/policies/refund-policy">Rückerstattungsrichtlinie</Link>)</td></tr>
          <tr><td>Rücksendeadresse</td><td>Deutschland — Rücksendungen nach Rücksprache an unsere Geschäftsadresse (siehe <Link href="/policies/impressum">Impressum</Link>)</td></tr>
          <tr><td>Rücksendekosten</td><td>Kostenlos</td></tr>
          <tr><td>Erstattungsdauer</td><td>3–7 Werktage nach Eingang der zurückgesandten Ware</td></tr>
          <tr><td>Zahlungsarten</td><td>Über SumUp u. a.: Visa, Mastercard, American Express, Apple Pay, Google Pay, Shop Pay (soweit im Zahlungsfenster verfügbar)</td></tr>
        </tbody></table>
        <h2>Versandgebiet</h2><p>Wir liefern nach <strong>Deutschland</strong> und in die <strong>Vereinigten Staaten</strong>. Bei Fragen: <a href="mailto:contact@marktio.shop">contact@marktio.shop</a></p>
        <h2>Versandart</h2><p>Lieferung per <strong>Spedition</strong> aus Deutschland. Die Spedition kann Sie zur Terminvereinbarung kontaktieren.</p>
        <h2>Produkte &amp; Lieferzeiten</h2>
        <table><thead><tr><th>Produkt</th><th>Bearbeitung</th><th>Lieferzeit (ca.)</th></tr></thead><tbody><tr><td>Marstek Venus E 3.0</td><td>1–2 Werktage</td><td>3–7 Werktage</td></tr><tr><td>Marstek B2500D</td><td>1–2 Werktage</td><td>3–7 Werktage</td></tr></tbody></table>
        <h2>Versandkosten</h2><p><strong>Kostenloser Versand</strong> für alle Bestellungen im angegebenen Liefergebiet. Im Checkout werden keine zusätzlichen Versandgebühren erhoben.</p>
        {back}
      </div>
    ),
    "uber-uns": (
      <div className="policy-content">
        <div className="section-label">Unternehmen</div><h1>Über uns</h1><div className="policy-date">Willkommen bei Marktio</div>
        <p>Bei Marktio setzen wir uns dafür ein, Ihnen ein reibungsloses und zuverlässiges Online-Shopping-Erlebnis zu bieten. Wir führen unsere Produkte aus <strong>Eigenlager</strong>; Versand erfolgt aus <strong>Deutschland</strong> in <strong>Euro (EUR)</strong>.</p>
        <h2>Unser Engagement</h2>
        <ul><li>Sichere Zahlungen über SumUp — u. a. Visa, Mastercard, American Express, Apple Pay, Google Pay, Shop Pay (soweit im Zahlungsfenster verfügbar)</li><li>Kostenloser Versand; Lieferzeit 3–7 Tage (Details: <Link href="/policies/shipping-policy">Versandrichtlinie</Link>)</li><li>Kostenlose Rückgabe innerhalb von 30 Tagen (Details: <Link href="/policies/refund-policy">Rückerstattungsrichtlinie</Link>)</li><li>Kundensupport Mo–Fr 9–18 Uhr</li><li>DSGVO-konformer Datenschutz</li></ul>
        <h2>Kontakt</h2>
        <p><strong>Marktio</strong><br />Inhaber: Winston Albert Frame<br />Kleine Alexanderstraße 10, 10178 Berlin, Deutschland<br /><a href="mailto:contact@marktio.shop">contact@marktio.shop</a> · <a href="tel:+49421167402">+49 0421 16 74 02</a><br />Mo–Fr 9:00–18:00 Uhr</p>
        {back}
      </div>
    ),
  };
  return pages[page] || null;
}

// ─── CART SIDEBAR ─────────────────────────────────────────────────────────────
function CartSidebar({ cart, open, onClose, onRemove, onCheckout }) {
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  return (
    <>
      <div className={`cart-overlay${open?" open":""}`} onClick={onClose} />
      <div className={`cart-sidebar${open?" open":""}`}>
        <div className="cart-header"><h3>🛒 Warenkorb</h3><div className="cart-close" onClick={onClose}>✕</div></div>
        <div className="cart-items">
          {cart.length === 0
            ? <div className="empty-cart"><div className="ec-icon">🛒</div><p>Ihr Warenkorb ist leer.</p></div>
            : cart.map((item,i)=>(
              <div key={i} className="cart-item">
                <img className="cart-item-img" src={item.img} alt={item.name} />
                <div className="cart-item-info">
                  <div className="cart-item-name">{item.name}</div>
                  <div style={{fontSize:".8rem",color:"var(--muted)"}}>Menge: {item.qty}</div>
                  <div className="cart-item-price">{fmt(item.price * item.qty)}</div>
                  <div className="cart-item-remove" onClick={()=>onRemove(i)}>Entfernen</div>
                </div>
              </div>
            ))
          }
        </div>
        {cart.length > 0 && (
          <div className="cart-footer">
            <div className="cart-total"><span>Gesamt (inkl. 0% MwSt.)</span><span>{fmt(total)}</span></div>
            <button className="cart-checkout-btn" onClick={onCheckout}>Zur Kasse →</button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState(() => pathToPage(window.location.pathname));
  const [cart, setCart] = useState(() => loadCartFromStorage());
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notif, setNotif] = useState({ show: false, msg: "", sub: "", type: "success" });

  useEffect(() => {
    const handler = () => {
      setPage(pathToPage(window.location.pathname));
      window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, cartToStoragePayload(cart));
    } catch (_) {}
  }, [cart]);

  const addToCart = useCallback((productId, qty = 1, silent = false) => {
    let blocked = false;
    let blockMsg = "";
    let replacedOther = false;

    setCart(prev => {
      if (prev.some((i) => i.id === productId)) {
        blocked = true;
        blockMsg = "Dieses Produkt ist bereits in Ihrem Warenkorb.";
        return prev;
      }
      if (prev.length > 0) replacedOther = true;
      return [{ ...PRODUCTS[productId], qty: 1 }];
    });

    if (blocked) {
      setNotif({ show: true, msg: blockMsg });
      setTimeout(() => setNotif(n => ({ ...n, show: false })), 3500);
      return;
    }

    if (!silent) {
      const name = PRODUCTS[productId].name.split("–")[0].trim();
      const msg = replacedOther
        ? `${name} ist jetzt im Warenkorb (vorheriges Produkt wurde ersetzt).`
        : `${name} zum Warenkorb hinzugefügt!`;
      setNotif({ show: true, msg });
      setTimeout(() => setNotif(n => ({ ...n, show: false })), 3000);
    }
  }, []);

  const navigate = useCallback((href) => {
    window.history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap";
    document.head.appendChild(link);
    // Add viewport meta tag for mobile
    if (!document.querySelector('meta[name="viewport"]')) {
      const meta = document.createElement("meta");
      meta.name = "viewport";
      meta.content = "width=device-width, initial-scale=1.0, maximum-scale=1.0";
      document.head.appendChild(meta);
    }
    // Performance: preconnect to SumUp to reduce first payment load.
    if (!document.querySelector('link[rel="preconnect"][href="https://pay.sumup.com"]')) {
      const pc = document.createElement("link");
      pc.rel = "preconnect";
      pc.href = "https://pay.sumup.com";
      pc.crossOrigin = "";
      document.head.appendChild(pc);
    }
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }), { threshold: 0.1 });
    document.querySelectorAll(".fade-in").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [page]);

  const removeFromCart = useCallback((index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  }, []);

  const totalItems = cart.reduce((s, i) => s + i.qty, 0);

  const renderPage = () => {
    if (page === "home") return <HomePage addToCart={addToCart} />;
    if (page === "products") return <ProductsPage addToCart={addToCart} />;
    if (page === "product-venus") return <ProductDetailPage addToCart={addToCart} productIndex={0} navigate={navigate} />;
    if (page === "product-b2500") return <ProductDetailPage addToCart={addToCart} productIndex={1} navigate={navigate} />;
    if (page === "checkout") return <CheckoutPage cart={cart} />;
    if (["impressum","contact","privacy-policy","terms-of-service","refund-policy","shipping-policy","uber-uns"].includes(page)) return <PolicyPage page={page} />;
    return <HomePage addToCart={addToCart} />;
  };

  const NAV_LINKS = [
    ["/", "Startseite"],
    ["/products", "Produkte"],
    [PRODUCTS[0].href, "Venus E 3.0"],
    [PRODUCTS[1].href, "B2500D"],
    ["/uber-uns", "Über uns"],
  ];

  const MOBILE_LINKS = [
    ["/", "Startseite"],
    ["/products", "Produkte"],
    ["/uber-uns", "Über uns"],
    ["/checkout", "Kasse"],
  ];

  const [navProgress, setNavProgress] = useState(0);
  const [navVisible, setNavVisible] = useState(false);

  useEffect(() => {
    setNavVisible(true);
    setNavProgress(0);
    const t1 = setTimeout(() => setNavProgress(100), 30);
    const t2 = setTimeout(() => setNavVisible(false), 500);
    const t3 = setTimeout(() => setNavProgress(0), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [page]);

  function ToastNotif({ notif }) {
    const CIRC = 2 * Math.PI * 11;
    const COLORS = { success: '#1D9E75', block: '#E24B4A', remove: '#888780' };
    const ICONS  = { success: '✓', block: '✕', remove: '−' };
    const [offset, setOffset] = useState(0);

    useEffect(() => {
      if (!notif.show) return;
      setOffset(0);
      const start = performance.now();
      let raf;
      const tick = (now) => {
        const p = Math.min((now - start) / 3000, 1);
        setOffset(CIRC * p);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [notif]);

    return (
      <div className={`notif${notif.show ? " show" : ""}`} style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ position: "relative", width: 28, height: 28, flexShrink: 0 }}>
          <svg width="28" height="28" viewBox="0 0 28 28" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="14" cy="14" r="11" fill="none" stroke="rgba(128,128,128,0.2)" strokeWidth="3"/>
            <circle cx="14" cy="14" r="11" fill="none"
              stroke={COLORS[notif.type] || COLORS.success}
              strokeWidth="3" strokeLinecap="round"
              strokeDasharray={CIRC} strokeDashoffset={offset}
            />
          </svg>
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color: COLORS[notif.type] }}>
            {ICONS[notif.type]}
          </div>
        </div>
        <div style={{minWidth:0}}>
          <div style={{ fontSize: ".9rem", fontWeight: 500 }}>{notif.msg}</div>
          {notif.sub && <div style={{ fontSize: ".8rem", opacity: .7 }}>{notif.sub}</div>}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="progress-bar" style={{ width: `${progress}%` }} />

      <ToastNotif notif={notif} />

      <CartSidebar cart={cart} open={cartOpen} onClose={()=>setCartOpen(false)} onRemove={removeFromCart} onCheckout={()=>{setCartOpen(false);navigate("/checkout");}} />

      <div className={`mobile-menu${mobileOpen?" open":""}`}>
        <span className="mobile-close" onClick={()=>setMobileOpen(false)}>✕</span>
        {MOBILE_LINKS.map(([href, label])=>(
          <Link key={href} href={href} onClick={()=>setMobileOpen(false)}>{label}</Link>
        ))}
        <Link href="/products" className="btn-primary" onClick={()=>setMobileOpen(false)}>Jetzt kaufen</Link>
      </div>

      <nav className={scrolled?"scrolled":""}>
        <div className="container">
          <div className="nav-inner">
            <Link href="/" className="logo">
              <div className="logo-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              Mark<span>tio</span>
            </Link>
            <div className="nav-links">
              {NAV_LINKS.map(([href, label])=>(
                <Link key={href} href={href}>{label}</Link>
              ))}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:".75rem",flexShrink:0}}>
              <div className="cart-btn" onClick={()=>setCartOpen(true)}>
                <span>🛒</span>
                <span style={{fontFamily:"'Syne',sans-serif",fontWeight:700}}>{totalItems}</span>
                {totalItems > 0 && <div className="cart-count-badge">{totalItems}</div>}
              </div>
              <Link href="/products" className="nav-cta">Shop</Link>
              <div className="hamburger" onClick={()=>setMobileOpen(true)}>
                <span /><span /><span />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {renderPage()}
    </>
  );
}